import { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import FormTemplateBuilder from "./FormTemplateBuilder";
import MaterialTypesTab from "@/modules/masterdata/MaterialTypes";
import SitesPage from "@/modules/facilities/Sites";
import ZonesPage from "@/modules/facilities/Zones";
import AreasPage from "@/modules/facilities/Areas";
import { 
  Settings as SettingsIcon, 
  Building2, 
  Plus, 
  Trash2, 
  Edit2, 
  Save, 
  Globe, 
  Bell, 
  Shield, 
  Loader2,
  Database,
  MapPin,
  Building,
  Layers,
  Grid3X3,
  Package,
  Activity,
  UserCog,
  Users2,
  Upload
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { Separator } from "@/components/ui/separator";
import { trpc } from "@/lib/trpc";
import { HierarchicalTypeManager } from "./components/HierarchicalTypeManager";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Languages, ExternalLink, FileText, X as XIcon, Tag, BookOpen, Video as VideoIcon } from "lucide-react";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { useAuth } from "@/utils/useAuth";
import { FioriPageHeader } from "@/components/fiori";
import { Link } from "wouter";

export default function Settings() {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'ar';
  
  // Fetch departments from backend
  const { data: departmentsData, isLoading: departmentsLoading, refetch: refetchDepartments } = trpc.departments.list.useQuery({});
  
  const [newDeptOpen, setNewDeptOpen] = useState(false);
  const [editDeptOpen, setEditDeptOpen] = useState(false);
  const [editingDept, setEditingDept] = useState<any>(null);
  const [newDept, setNewDept] = useState({ name: "", costCenter: "", description: "" });

  const createDeptMutation = trpc.departments.create.useMutation({
    onSuccess: () => {
      toast.success(t("settings.dept.created"), { description: `${newDept.name} ${t("settings.dept.createdDesc")}` });
      refetchDepartments();
      setNewDeptOpen(false);
      setNewDept({ name: "", costCenter: "", description: "" });
    },
    onError: (error) => { toast.error(t("settings.dept.createFailed"), { description: error.message }); }
  });

  const updateDeptMutation = trpc.departments.update.useMutation({
    onSuccess: () => {
      toast.success(t("settings.dept.updated"));
      refetchDepartments();
      setEditDeptOpen(false);
      setEditingDept(null);
    },
    onError: (error) => { toast.error(t("settings.dept.updateFailed"), { description: error.message }); }
  });

  const deleteDeptMutation = trpc.departments.delete.useMutation({
    onSuccess: () => {
      toast.success(t("settings.dept.removed"));
      refetchDepartments();
    },
    onError: (error) => { toast.error(t("settings.dept.deleteFailed"), { description: error.message }); }
  });

  const departments = departmentsData || [];

  const handleAddDepartment = (e: React.FormEvent) => {
    e.preventDefault();
    createDeptMutation.mutate({ name: newDept.name, costCenter: newDept.costCenter || undefined, description: newDept.description || undefined });
  };

  const handleEditDepartment = (dept: any) => {
    setEditingDept({ id: dept.id, name: dept.name, costCenter: dept.costCenter || "", description: dept.description || "" });
    setEditDeptOpen(true);
  };

  const handleUpdateDepartment = (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingDept) return;
    updateDeptMutation.mutate({ id: editingDept.id, name: editingDept.name, costCenter: editingDept.costCenter || undefined, description: editingDept.description || undefined });
  };

  const handleDeleteDept = (id: number) => {
    if (confirm(t("settings.dept.deleteConfirm"))) { deleteDeptMutation.mutate({ id }); }
  };

  // Security settings
  const { data: securityData, refetch: refetchSecurity } = trpc.settings.getSecurityPolicies.useQuery();
  const [securitySettings, setSecuritySettings] = useState({ passwordExpiry: 90, sessionTimeout: 30, mfaEnabled: false });

  useEffect(() => {
    if (securityData) {
      setSecuritySettings({ passwordExpiry: securityData.passwordExpiry, sessionTimeout: securityData.sessionTimeout, mfaEnabled: securityData.mfaEnabled });
    }
  }, [securityData]);

  const updateSecurityMutation = trpc.settings.updateSecurityPolicies.useMutation({
    onSuccess: () => {
      toast.success(t("settings.security.updated"), { description: t("settings.security.updatedDesc") });
      refetchSecurity();
    },
    onError: (error) => { toast.error(t("settings.security.updateFailed"), { description: error.message }); }
  });

  const handleSaveSecuritySettings = () => {
    updateSecurityMutation.mutate({ passwordExpiry: securitySettings.passwordExpiry, sessionTimeout: securitySettings.sessionTimeout, mfaEnabled: securitySettings.mfaEnabled });
  };

  // ============================================================================
  // MASTER DATA STATE & QUERIES
  // ============================================================================
  const [masterDataTab, setMasterDataTab] = useState("activities");
  const [activitiesSubTab, setActivitiesSubTab] = useState("main");
  const [locationSubTab, setLocationSubTab] = useState("countries");

  // Main Activities
  const { data: mainActivitiesData, isLoading: mainActivitiesLoading, refetch: refetchMainActivities } = trpc.masterData.getAllMainActivities.useQuery();
  const [newMainActivityOpen, setNewMainActivityOpen] = useState(false);
  const [editMainActivityOpen, setEditMainActivityOpen] = useState(false);
  const [editingMainActivity, setEditingMainActivity] = useState<any>(null);
  const [newMainActivity, setNewMainActivity] = useState({ name: "", nameAr: "", description: "", icon: "", color: "" });
  
  const createMainActivityMutation = trpc.masterData.createMainActivity.useMutation({
    onSuccess: () => {
      toast.success(t("settings.masterData.mainActivityCreated"));
      refetchMainActivities();
      setNewMainActivityOpen(false);
      setNewMainActivity({ name: "", nameAr: "", description: "", icon: "", color: "" });
    },
    onError: (error) => toast.error(t("settings.masterData.createFailed"), { description: error.message })
  });
  
  const updateMainActivityMutation = trpc.masterData.updateMainActivity.useMutation({
    onSuccess: () => {
      toast.success(t("settings.masterData.mainActivityUpdated"));
      refetchMainActivities();
      setEditMainActivityOpen(false);
      setEditingMainActivity(null);
    },
    onError: (error) => toast.error(t("settings.masterData.updateFailed"), { description: error.message })
  });
  
  const deleteMainActivityMutation = trpc.masterData.deleteMainActivity.useMutation({
    onSuccess: () => {
      toast.success(t("settings.masterData.mainActivityDeleted"));
      refetchMainActivities();
    },
    onError: (error) => toast.error(t("settings.masterData.deleteFailed"), { description: error.message })
  });

  // Sub Activities
  const { data: subActivitiesData, isLoading: subActivitiesLoading, refetch: refetchSubActivities } = trpc.masterData.getAllSubActivities.useQuery();
  const [newSubActivityOpen, setNewSubActivityOpen] = useState(false);
  const [editSubActivityOpen, setEditSubActivityOpen] = useState(false);
  const [editingSubActivity, setEditingSubActivity] = useState<any>(null);
  const [newSubActivity, setNewSubActivity] = useState({ mainActivityId: 0, name: "", nameAr: "", description: "", needsRFC: false, needsHRS: false, needsMOP: false, needsMHV: false, needsRoomSelection: false, requiresMOP: false, requiresPermit: false, riskLevel: "low" as const });
  
  const createSubActivityMutation = trpc.masterData.createSubActivity.useMutation({
    onSuccess: () => {
      toast.success(t("settings.masterData.subActivityCreated"));
      refetchSubActivities(); refetchMainActivities();
      setNewSubActivityOpen(false);
      setNewSubActivity({ mainActivityId: 0, name: "", nameAr: "", description: "", needsRFC: false, needsHRS: false, needsMOP: false, needsMHV: false, needsRoomSelection: false, requiresMOP: false, requiresPermit: false, riskLevel: "low" });
    },
    onError: (error) => toast.error(t("settings.masterData.createFailed"), { description: error.message })
  });
  
  const updateSubActivityMutation = trpc.masterData.updateSubActivity.useMutation({
    onSuccess: () => {
      toast.success(t("settings.masterData.subActivityUpdated"));
      refetchSubActivities();
      setEditSubActivityOpen(false); setEditingSubActivity(null);
    },
    onError: (error) => toast.error(t("settings.masterData.updateFailed"), { description: error.message })
  });
  
  const deleteSubActivityMutation = trpc.masterData.deleteSubActivity.useMutation({
    onSuccess: () => {
      toast.success(t("settings.masterData.subActivityDeleted"));
      refetchSubActivities(); refetchMainActivities();
    },
    onError: (error) => toast.error(t("settings.masterData.deleteFailed"), { description: error.message })
  });

  // Role Types
  const { data: roleTypesData, isLoading: roleTypesLoading, refetch: refetchRoleTypes } = trpc.masterData.getAllRoleTypes.useQuery();
  const [newRoleTypeOpen, setNewRoleTypeOpen] = useState(false);
  const [editRoleTypeOpen, setEditRoleTypeOpen] = useState(false);
  const [editingRoleType, setEditingRoleType] = useState<any>(null);
  const [newRoleType, setNewRoleType] = useState({ name: "", nameAr: "", description: "", category: "internal" as const, accessLevel: "standard" as const });
  
  const createRoleTypeMutation = trpc.masterData.createRoleType.useMutation({
    onSuccess: () => {
      toast.success(t("settings.masterData.roleTypeCreated"));
      refetchRoleTypes(); setNewRoleTypeOpen(false);
      setNewRoleType({ name: "", nameAr: "", description: "", category: "internal", accessLevel: "standard" });
    },
    onError: (error) => toast.error(t("settings.masterData.createFailed"), { description: error.message })
  });
  
  const updateRoleTypeMutation = trpc.masterData.updateRoleType.useMutation({
    onSuccess: () => {
      toast.success(t("settings.masterData.roleTypeUpdated"));
      refetchRoleTypes(); setEditRoleTypeOpen(false); setEditingRoleType(null);
    },
    onError: (error) => toast.error(t("settings.masterData.updateFailed"), { description: error.message })
  });
  
  const deleteRoleTypeMutation = trpc.masterData.deleteRoleType.useMutation({
    onSuccess: () => { toast.success(t("settings.masterData.roleTypeDeleted")); refetchRoleTypes(); },
    onError: (error) => toast.error(t("settings.masterData.deleteFailed"), { description: error.message })
  });

  // Approvers
  const { data: approversData, isLoading: approversLoading, refetch: refetchApprovers } = trpc.masterData.getAllApprovers.useQuery();
  const { data: usersForApprovers } = trpc.users.list.useQuery({ limit: 100 });
  const { data: sitesForApprovers } = trpc.sites.getAll.useQuery();
  const [newApproverOpen, setNewApproverOpen] = useState(false);
  const [editApproverOpen, setEditApproverOpen] = useState(false);
  const [editingApprover, setEditingApprover] = useState<any>(null);
  const [newApprover, setNewApprover] = useState<{ userId: number; siteId?: number; approvalLevel: number; canApproveEmergency: boolean; canApproveVIP: boolean }>({ userId: 0, approvalLevel: 1, canApproveEmergency: false, canApproveVIP: false });
  
  const createApproverMutation = trpc.masterData.createApprover.useMutation({
    onSuccess: () => {
      toast.success(t("settings.masterData.approverCreated"));
      refetchApprovers(); setNewApproverOpen(false);
      setNewApprover({ userId: 0, approvalLevel: 1, canApproveEmergency: false, canApproveVIP: false });
    },
    onError: (error) => toast.error(t("settings.masterData.createFailed"), { description: error.message })
  });
  
  const updateApproverMutation = trpc.masterData.updateApprover.useMutation({
    onSuccess: () => {
      toast.success(t("settings.masterData.approverUpdated"));
      refetchApprovers(); setEditApproverOpen(false); setEditingApprover(null);
    },
    onError: (error) => toast.error(t("settings.masterData.updateFailed"), { description: error.message })
  });
  
  const deleteApproverMutation = trpc.masterData.deleteApprover.useMutation({
    onSuccess: () => { toast.success(t("settings.masterData.approverDeleted")); refetchApprovers(); },
    onError: (error) => toast.error(t("settings.masterData.deleteFailed"), { description: error.message })
  });

  // Companies
  const { data: companiesData, isLoading: companiesLoading, refetch: refetchCompanies } = trpc.masterData.getAllCompanies.useQuery();
  const [newCompanyOpen, setNewCompanyOpen] = useState(false);
  const [editCompanyOpen, setEditCompanyOpen] = useState(false);
  const [editingCompany, setEditingCompany] = useState<any>(null);
  const [companiesFilter, setCompaniesFilter] = useState<"all" | "contractor" | "subcontractor" | "client">("all");
  const [newCompany, setNewCompany] = useState<{
    code: string; name: string; nameAr: string; type: "contractor" | "subcontractor" | "client";
    parentCompanyId: number | undefined;
    contactPersonName: string; contactPersonEmail: string; contactPersonPhone: string; contactPersonPosition: string;
    contractReference: string; contractStartDate: string; contractEndDate: string;
    address: string; city: string; country: string; registrationNumber: string;
    status: "active" | "inactive" | "suspended"; notes: string;
  }>({
    code: "", name: "", nameAr: "", type: "contractor", parentCompanyId: undefined,
    contactPersonName: "", contactPersonEmail: "", contactPersonPhone: "", contactPersonPosition: "",
    contractReference: "", contractStartDate: "", contractEndDate: "",
    address: "", city: "", country: "", registrationNumber: "", status: "active", notes: ""
  });

  const createCompanyMutation = trpc.masterData.createCompany.useMutation({
    onSuccess: () => {
      toast.success(t("settings.masterData.companyCreated"));
      refetchCompanies(); setNewCompanyOpen(false);
      setNewCompany({ code: "", name: "", nameAr: "", type: "contractor", parentCompanyId: undefined, contactPersonName: "", contactPersonEmail: "", contactPersonPhone: "", contactPersonPosition: "", contractReference: "", contractStartDate: "", contractEndDate: "", address: "", city: "", country: "", registrationNumber: "", status: "active", notes: "" });
    },
    onError: (error) => toast.error(t("settings.masterData.createFailed"), { description: error.message })
  });

  const updateCompanyMutation = trpc.masterData.updateCompany.useMutation({
    onSuccess: () => {
      toast.success(t("settings.masterData.companyUpdated"));
      refetchCompanies(); setEditCompanyOpen(false); setEditingCompany(null);
    },
    onError: (error) => toast.error(t("settings.masterData.updateFailed"), { description: error.message })
  });

  const deleteCompanyMutation = trpc.masterData.deleteCompany.useMutation({
    onSuccess: () => { toast.success(t("settings.masterData.companyDeactivated")); refetchCompanies(); },
    onError: (error) => toast.error(t("settings.masterData.deleteFailed"), { description: error.message })
  });

  const filteredCompanies = companiesData?.filter(c => companiesFilter === "all" || c.type === companiesFilter) || [];
  
  // Countries
  const { data: countriesData, isLoading: countriesLoading, refetch: refetchCountries } = trpc.masterData.getAllCountries.useQuery();
  const [newCountryOpen, setNewCountryOpen] = useState(false);
  const [editCountryOpen, setEditCountryOpen] = useState(false);
  const [editingCountry, setEditingCountry] = useState<any>(null);
  const [newCountry, setNewCountry] = useState({ code: "", name: "" });
  
  const createCountryMutation = trpc.masterData.createCountry.useMutation({
    onSuccess: () => { toast.success(t("settings.masterData.countryCreated")); refetchCountries(); setNewCountryOpen(false); setNewCountry({ code: "", name: "" }); },
    onError: (error) => toast.error(t("settings.masterData.createFailed"), { description: error.message })
  });
  const updateCountryMutation = trpc.masterData.updateCountry.useMutation({
    onSuccess: () => { toast.success(t("settings.masterData.countryUpdated")); refetchCountries(); setEditCountryOpen(false); setEditingCountry(null); },
    onError: (error) => toast.error(t("settings.masterData.updateFailed"), { description: error.message })
  });
  const deleteCountryMutation = trpc.masterData.deleteCountry.useMutation({
    onSuccess: () => { toast.success(t("settings.masterData.countryDeleted")); refetchCountries(); },
    onError: (error) => toast.error(t("settings.masterData.deleteFailed"), { description: error.message })
  });

  // Regions
  const { data: regionsData, isLoading: regionsLoading, refetch: refetchRegions } = trpc.masterData.getAllRegions.useQuery();
  const [newRegionOpen, setNewRegionOpen] = useState(false);
  const [editRegionOpen, setEditRegionOpen] = useState(false);
  const [editingRegion, setEditingRegion] = useState<any>(null);
  const [newRegion, setNewRegion] = useState({ code: "", name: "" });
  
  const createRegionMutation = trpc.masterData.createRegion.useMutation({
    onSuccess: () => { toast.success(t("settings.masterData.regionCreated")); refetchRegions(); setNewRegionOpen(false); setNewRegion({ code: "", name: "" }); },
    onError: (error) => toast.error(t("settings.masterData.createFailed"), { description: error.message })
  });
  const updateRegionMutation = trpc.masterData.updateRegion.useMutation({
    onSuccess: () => { toast.success(t("settings.masterData.regionUpdated")); refetchRegions(); setEditRegionOpen(false); setEditingRegion(null); },
    onError: (error) => toast.error(t("settings.masterData.updateFailed"), { description: error.message })
  });
  const deleteRegionMutation = trpc.masterData.deleteRegion.useMutation({
    onSuccess: () => { toast.success(t("settings.masterData.regionDeleted")); refetchRegions(); },
    onError: (error) => toast.error(t("settings.masterData.deleteFailed"), { description: error.message })
  });

  // Cities
  const { data: citiesData, isLoading: citiesLoading, refetch: refetchCities } = trpc.masterData.getAllCities.useQuery();
  const [newCityOpen, setNewCityOpen] = useState(false);
  const [editCityOpen, setEditCityOpen] = useState(false);
  const [editingCity, setEditingCity] = useState<any>(null);
  const [newCity, setNewCity] = useState({ countryId: 0, name: "" });
  
  const createCityMutation = trpc.masterData.createCity.useMutation({
    onSuccess: () => { toast.success(t("settings.masterData.cityCreated")); refetchCities(); setNewCityOpen(false); setNewCity({ countryId: 0, name: "" }); },
    onError: (error) => toast.error(t("settings.masterData.createFailed"), { description: error.message })
  });
  const updateCityMutation = trpc.masterData.updateCity.useMutation({
    onSuccess: () => { toast.success(t("settings.masterData.cityUpdated")); refetchCities(); setEditCityOpen(false); setEditingCity(null); },
    onError: (error) => toast.error(t("settings.masterData.updateFailed"), { description: error.message })
  });
  const deleteCityMutation = trpc.masterData.deleteCity.useMutation({
    onSuccess: () => { toast.success(t("settings.masterData.cityDeleted")); refetchCities(); },
    onError: (error) => toast.error(t("settings.masterData.deleteFailed"), { description: error.message })
  });

  // Site Types
  const { data: siteTypesData, isLoading: siteTypesLoading, refetch: refetchSiteTypes } = trpc.masterData.getAllSiteTypes.useQuery();
  const [newSiteTypeOpen, setNewSiteTypeOpen] = useState(false);
  const [editSiteTypeOpen, setEditSiteTypeOpen] = useState(false);
  const [editingSiteType, setEditingSiteType] = useState<any>(null);
  const [newSiteType, setNewSiteType] = useState({ code: "", name: "", description: "" });
  
  const createSiteTypeMutation = trpc.masterData.createSiteType.useMutation({
    onSuccess: () => { toast.success(t("settings.masterData.siteTypeCreated")); refetchSiteTypes(); setNewSiteTypeOpen(false); setNewSiteType({ code: "", name: "", description: "" }); },
    onError: (error) => toast.error(t("settings.masterData.createFailed"), { description: error.message })
  });
  const updateSiteTypeMutation = trpc.masterData.updateSiteType.useMutation({
    onSuccess: () => { toast.success(t("settings.masterData.siteTypeUpdated")); refetchSiteTypes(); setEditSiteTypeOpen(false); setEditingSiteType(null); },
    onError: (error) => toast.error(t("settings.masterData.updateFailed"), { description: error.message })
  });
  const deleteSiteTypeMutation = trpc.masterData.deleteSiteType.useMutation({
    onSuccess: () => { toast.success(t("settings.masterData.siteTypeDeleted")); refetchSiteTypes(); },
    onError: (error) => toast.error(t("settings.masterData.deleteFailed"), { description: error.message })
  });

  // Zone Types
  const { data: zoneTypesData, isLoading: zoneTypesLoading, refetch: refetchZoneTypes } = trpc.masterData.getAllZoneTypes.useQuery();
  const [newZoneTypeOpen, setNewZoneTypeOpen] = useState(false);
  const [editZoneTypeOpen, setEditZoneTypeOpen] = useState(false);
  const [editingZoneType, setEditingZoneType] = useState<any>(null);
  const [newZoneType, setNewZoneType] = useState({ code: "", name: "", description: "" });
  
  const createZoneTypeMutation = trpc.masterData.createZoneType.useMutation({
    onSuccess: () => { toast.success(t("settings.masterData.zoneTypeCreated")); refetchZoneTypes(); setNewZoneTypeOpen(false); setNewZoneType({ code: "", name: "", description: "" }); },
    onError: (error) => toast.error(t("settings.masterData.createFailed"), { description: error.message })
  });
  const updateZoneTypeMutation = trpc.masterData.updateZoneType.useMutation({
    onSuccess: () => { toast.success(t("settings.masterData.zoneTypeUpdated")); refetchZoneTypes(); setEditZoneTypeOpen(false); setEditingZoneType(null); },
    onError: (error) => toast.error(t("settings.masterData.updateFailed"), { description: error.message })
  });
  const deleteZoneTypeMutation = trpc.masterData.deleteZoneType.useMutation({
    onSuccess: () => { toast.success(t("settings.masterData.zoneTypeDeleted")); refetchZoneTypes(); },
    onError: (error) => toast.error(t("settings.masterData.deleteFailed"), { description: error.message })
  });

  // Area Types
  const { data: areaTypesData, isLoading: areaTypesLoading, refetch: refetchAreaTypes } = trpc.masterData.getAllAreaTypes.useQuery();
  const [newAreaTypeOpen, setNewAreaTypeOpen] = useState(false);
  const [editAreaTypeOpen, setEditAreaTypeOpen] = useState(false);
  const [editingAreaType, setEditingAreaType] = useState<any>(null);
  const [newAreaType, setNewAreaType] = useState({ code: "", name: "", description: "" });
  
  const createAreaTypeMutation = trpc.masterData.createAreaType.useMutation({
    onSuccess: () => { toast.success(t("settings.masterData.areaTypeCreated")); refetchAreaTypes(); setNewAreaTypeOpen(false); setNewAreaType({ code: "", name: "", description: "" }); },
    onError: (error) => toast.error(t("settings.masterData.createFailed"), { description: error.message })
  });
  const updateAreaTypeMutation = trpc.masterData.updateAreaType.useMutation({
    onSuccess: () => { toast.success(t("settings.masterData.areaTypeUpdated")); refetchAreaTypes(); setEditAreaTypeOpen(false); setEditingAreaType(null); },
    onError: (error) => toast.error(t("settings.masterData.updateFailed"), { description: error.message })
  });
  const deleteAreaTypeMutation = trpc.masterData.deleteAreaType.useMutation({
    onSuccess: () => { toast.success(t("settings.masterData.areaTypeDeleted")); refetchAreaTypes(); },
    onError: (error) => toast.error(t("settings.masterData.deleteFailed"), { description: error.message })
  });

  // ============================================================================
  // KNOWLEDGE CENTER STATE & QUERIES
  // ============================================================================
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const fileInputRef = useRef<HTMLInputElement>(null);

  const { data: knowledgeContents = [], isLoading: knowledgeLoading, refetch: refetchKnowledge } = trpc.knowledge.getAllContentAdmin.useQuery(undefined, { enabled: isAdmin });
  const { data: knowledgeCategories = [], refetch: refetchCategories } = trpc.knowledge.getCategories.useQuery();
  const { data: knowledgeTags = [], refetch: refetchTags } = trpc.knowledge.getTags.useQuery();

  const [knowledgeForm, setKnowledgeForm] = useState({ title: "", description: "", category: "", contentType: "PDF" as "PDF" | "Video", tags: "", mandatoryAck: false, url: "" });
  const [knowledgeFile, setKnowledgeFile] = useState<File | null>(null);
  const [isUploadingKnowledge, setIsUploadingKnowledge] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState("");
  const BUILTIN_CATEGORIES = ["Policies", "SOPs", "Technical Guides", "HR & Onboarding", "Safety", "General"];

  const createKnowledgeMutation = trpc.knowledge.createContent.useMutation({
    onSuccess: () => {
      toast.success(t("settings.knowledge.uploadSuccess"));
      refetchKnowledge(); refetchTags();
      setKnowledgeForm({ title: "", description: "", category: "", contentType: "PDF", tags: "", mandatoryAck: false, url: "" });
      setKnowledgeFile(null);
      if (fileInputRef.current) fileInputRef.current.value = "";
      setIsUploadingKnowledge(false);
    },
    onError: (error) => { toast.error(t("settings.knowledge.uploadFailed"), { description: error.message }); setIsUploadingKnowledge(false); },
  });

  const deleteKnowledgeMutation = trpc.knowledge.deleteContent.useMutation({
    onSuccess: () => { toast.success(t("settings.knowledge.deleted")); refetchKnowledge(); },
    onError: (error) => toast.error(t("settings.knowledge.deleteFailed"), { description: error.message }),
  });

  const updateKnowledgeStatusMutation = trpc.knowledge.updateStatus.useMutation({
    onSuccess: () => { toast.success(t("settings.knowledge.statusUpdated")); refetchKnowledge(); },
    onError: (error) => toast.error(t("settings.knowledge.statusUpdateFailed"), { description: error.message }),
  });

  const createCategoryMutation = trpc.knowledge.createCategory.useMutation({
    onSuccess: () => { toast.success(t("settings.knowledge.categoryCreated")); refetchCategories(); setNewCategoryName(""); },
    onError: (error) => toast.error(t("settings.knowledge.categoryCreateFailed"), { description: error.message }),
  });

  const deleteCategoryMutation = trpc.knowledge.deleteCategory.useMutation({
    onSuccess: () => { toast.success(t("settings.knowledge.categoryDeleted")); refetchCategories(); },
    onError: (error) => toast.error(t("settings.knowledge.categoryDeleteFailed"), { description: error.message }),
  });

  async function handleKnowledgeUpload(e: React.FormEvent) {
    e.preventDefault();
    if (!knowledgeForm.title || !knowledgeForm.category || !knowledgeForm.contentType) {
      toast.error(t("settings.knowledge.fillRequired")); return;
    }
    if (!knowledgeFile && !knowledgeForm.url) {
      toast.error(t("settings.knowledge.provideFileOrUrl")); return;
    }
    setIsUploadingKnowledge(true);
    try {
      let uploadPayload: { fileName: string; mimeType: string; base64Data: string } | undefined;
      if (knowledgeFile) {
        const MAX = 45 * 1024 * 1024;
        if (knowledgeFile.size > MAX) { toast.error(t("settings.knowledge.fileTooLarge")); setIsUploadingKnowledge(false); return; }
        const base64Data = await new Promise<string>((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => { const result = reader.result as string; const [, b64 = ""] = result.split(",", 2); resolve(b64); };
          reader.onerror = () => reject(reader.error);
          reader.readAsDataURL(knowledgeFile);
        });
        uploadPayload = { fileName: knowledgeFile.name, mimeType: knowledgeFile.type, base64Data };
      }
      createKnowledgeMutation.mutate({
        title: knowledgeForm.title, description: knowledgeForm.description || undefined,
        category: knowledgeForm.category, contentType: knowledgeForm.contentType,
        tags: knowledgeForm.tags || undefined, mandatoryAck: knowledgeForm.mandatoryAck,
        url: knowledgeForm.url || undefined, upload: uploadPayload,
      });
    } catch (err: any) {
      toast.error(t("settings.knowledge.fileReadFailed"), { description: err.message });
      setIsUploadingKnowledge(false);
    }
  }

  return (
    <div className="-mx-4 md:-mx-6 -mb-4 md:-mb-6 min-h-[calc(100vh-4rem)] bg-white px-4 md:px-6">
      <FioriPageHeader
        title={t("settings.title")}
        subtitle={t("settings.subtitle")}
        icon={<SettingsIcon className="h-5 w-5" />}
      />

        <Tabs defaultValue="masterdata" className="w-full">
        <div className="bg-white border border-[#E0E0E0] rounded-t-lg px-2 sm:px-4 pt-3 mb-0 overflow-x-auto scrollbar-none">
          <TabsList className="bg-transparent h-auto p-0 gap-0.5 sm:gap-1 flex-nowrap min-w-max">
            <TabsTrigger value="masterdata">{t("settings.tabs.masterData")}</TabsTrigger>
            <TabsTrigger value="translations">{t("settings.tabs.translations")}</TabsTrigger>
            <TabsTrigger value="security">{t("settings.tabs.security")}</TabsTrigger>
            <TabsTrigger value="forms">{t("settings.tabs.formTemplates")}</TabsTrigger>
            <TabsTrigger value="knowledge">{t("settings.tabs.knowledgeCenter")}</TabsTrigger>
          </TabsList>
        </div>
        

        {/* ============ TRANSLATIONS TAB ============ */}
        <TabsContent value="translations" className="mt-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Languages className="h-5 w-5 text-[#5B2C93]" />
                {t("settings.translations.title")}
              </CardTitle>
              <CardDescription>{t("settings.translations.desc")}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid gap-4 md:grid-cols-2">
                <Card className="border-dashed">
                  <CardHeader className="pb-3">
                    <CardTitle className="text-base">{t("settings.translations.supportedLangs")}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="flex flex-wrap gap-2">
                      <Badge variant="outline" className="px-3 py-1">
                        <span className="mr-2">🇬🇧</span> English (EN)
                      </Badge>
                      <Badge variant="outline" className="px-3 py-1">
                        <span className="mr-2">🇸🇦</span> Arabic (AR)
                      </Badge>
                    </div>
                  </CardContent>
                </Card>
                <Card className="border-dashed">
                  <CardHeader className="pb-3">
                    <CardTitle className="text-base">{t("settings.translations.translationStatus")}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      <div className="flex justify-between text-sm">
                        <span>{t("settings.translations.english")}</span>
                        <span className="text-[#059669] font-medium">100% {t("settings.translations.complete")}</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span>{t("settings.translations.arabic")}</span>
                        <span className="text-[#D97706] font-medium">~85% {t("settings.translations.complete")}</span>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
              <Separator />
              <div className="flex flex-col gap-4">
                <p className="text-sm text-[#6B6B6B]">{t("settings.translations.managerDesc")}</p>
                <Link href="/settings/translations">
                  <Button className="gap-2 bg-[#5B2C93] hover:bg-[#5B2C93]">
                    <Languages className="h-4 w-4" />
                    {t("settings.translations.openManager")}
                    <ExternalLink className="h-4 w-4" />
                  </Button>
                </Link>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* DEPARTMENTS TAB REMOVED */}
        {false && <TabsContent value="departments" className="mt-6">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <Building2 className="h-5 w-5 text-[#5B2C93]" />
                  {t("settings.dept.management")}
                </CardTitle>
                <CardDescription>{t("settings.dept.managementDesc")}</CardDescription>
              </div>
              <Dialog open={newDeptOpen} onOpenChange={setNewDeptOpen}>
                <DialogTrigger asChild>
                  <Button className="bg-[#5B2C93] hover:bg-[#5B2C93] gap-2">
                    <Plus className="h-4 w-4" /> {t("settings.dept.add")}
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <form onSubmit={handleAddDepartment}>
                    <DialogHeader>
                      <DialogTitle>{t("settings.dept.addNew")}</DialogTitle>
                      <DialogDescription>{t("settings.dept.addNewDesc")}</DialogDescription>
                    </DialogHeader>
                    <div className="grid gap-4 py-4">
                      <div className="space-y-2">
                        <Label htmlFor="deptName">{t("settings.dept.name")}</Label>
                        <Input id="deptName" placeholder={t("settings.dept.namePlaceholder")} value={newDept.name} onChange={e => setNewDept({...newDept, name: e.target.value})} required />
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label htmlFor="deptCode">{t("settings.dept.costCenter")}</Label>
                          <Input id="deptCode" placeholder={t("settings.dept.costCenterPlaceholder")} value={newDept.costCenter} onChange={e => setNewDept({...newDept, costCenter: e.target.value})} />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="deptDesc">{t("common.description")}</Label>
                          <Input id="deptDesc" placeholder={t("common.briefDescription")} value={newDept.description} onChange={e => setNewDept({...newDept, description: e.target.value})} />
                        </div>
                      </div>
                    </div>
                    <DialogFooter>
                      <Button type="button" variant="outline" onClick={() => setNewDeptOpen(false)}>{t("common.cancel")}</Button>
                      <Button type="submit" className="bg-[#5B2C93] hover:bg-[#5B2C93]" disabled={createDeptMutation.isPending}>
                        {createDeptMutation.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                        {t("settings.dept.create")}
                      </Button>
                    </DialogFooter>
                  </form>
                </DialogContent>
              </Dialog>
            </CardHeader>
            <CardContent>
              {departmentsLoading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="h-8 w-8 animate-spin text-[#5B2C93]" />
                  <span className="ml-2 text-[#6B6B6B]">{t("settings.dept.loading")}</span>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>{t("common.id")}</TableHead>
                      <TableHead>{t("settings.dept.name")}</TableHead>
                      <TableHead>{t("settings.dept.costCenter")}</TableHead>
                      <TableHead>{t("common.status")}</TableHead>
                      <TableHead className="text-right">{t("common.actions")}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {departments.map((dept) => (
                      <TableRow key={dept.id}>
                        <TableCell className="font-mono text-xs">#{dept.id}</TableCell>
                        <TableCell className="font-medium">{dept.name}</TableCell>
                        <TableCell>{dept.costCenter || "—"}</TableCell>
                        <TableCell>
                          <span className={`px-2 py-1 rounded-full text-xs ${dept.isActive ? 'bg-[#D1FAE5] text-[#059669]' : 'bg-[#FFE5E5] text-[#FF6B6B]'}`}>
                            {dept.isActive ? t("common.active") : t("common.inactive")}
                          </span>
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-2">
                            <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => handleEditDepartment(dept)}>
                              <Edit2 className="h-4 w-4 text-[#6B6B6B]" />
                            </Button>
                            <Button variant="ghost" size="icon" className="h-8 w-8 hover:text-[#FF6B6B] hover:bg-[#FFE5E5]" onClick={() => handleDeleteDept(dept.id)} disabled={deleteDeptMutation.isPending}>
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                    {departments.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={5} className="text-center py-8 text-[#6B6B6B]">
                          {t("settings.dept.empty")}
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>

          {/* Edit Department Dialog */}
          <Dialog open={editDeptOpen} onOpenChange={setEditDeptOpen}>
            <DialogContent>
              <form onSubmit={handleUpdateDepartment}>
                <DialogHeader>
                  <DialogTitle>{t("settings.dept.edit")}</DialogTitle>
                  <DialogDescription>{t("settings.dept.editDesc")}</DialogDescription>
                </DialogHeader>
                {editingDept && (
                  <div className="grid gap-4 py-4">
                    <div className="space-y-2">
                      <Label htmlFor="editDeptName">{t("settings.dept.name")}</Label>
                      <Input id="editDeptName" value={editingDept.name} onChange={e => setEditingDept({...editingDept, name: e.target.value})} required />
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="editDeptCode">{t("settings.dept.costCenter")}</Label>
                        <Input id="editDeptCode" value={editingDept.costCenter} onChange={e => setEditingDept({...editingDept, costCenter: e.target.value})} />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="editDeptDesc">{t("common.description")}</Label>
                        <Input id="editDeptDesc" value={editingDept.description} onChange={e => setEditingDept({...editingDept, description: e.target.value})} />
                      </div>
                    </div>
                  </div>
                )}
                <DialogFooter>
                  <Button type="button" variant="outline" onClick={() => setEditDeptOpen(false)}>{t("common.cancel")}</Button>
                  <Button type="submit" className="bg-[#5B2C93] hover:bg-[#5B2C93]" disabled={updateDeptMutation.isPending}>
                    {updateDeptMutation.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                    {t("common.saveChanges")}
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        </TabsContent>}

        {/* ============ MASTER DATA TAB ============ */}
        <TabsContent value="masterdata" className="mt-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Database className="h-5 w-5 text-[#5B2C93]" />
                {t("settings.masterData.title")}
              </CardTitle>
              <CardDescription>{t("settings.masterData.desc")}</CardDescription>
            </CardHeader>
            <CardContent>
              <Tabs value={masterDataTab} onValueChange={setMasterDataTab} className="w-full">
                <div className="overflow-x-auto border-b border-[#E0E0E0] mb-6 -mx-4 px-4 md:-mx-6 md:px-6">
                  <TabsList className="flex gap-1 h-auto p-0 bg-transparent flex-nowrap justify-start [&>button]:gap-2 [&>button]:whitespace-nowrap">
                    <TabsTrigger value="activities" className="gap-1">
                      <Activity className="h-4 w-4" /> {t("settings.masterData.activities")}
                    </TabsTrigger>
                    <TabsTrigger value="roletypes" className="gap-1">
                      <UserCog className="h-4 w-4" /> {t("settings.masterData.roleTypes")}
                    </TabsTrigger>
                    <TabsTrigger value="approvers" className="gap-1">
                      <Users2 className="h-4 w-4" /> {t("settings.masterData.approvers")}
                    </TabsTrigger>
                    <TabsTrigger value="location" className="gap-1">
                      <MapPin className="h-4 w-4" /> Location
                    </TabsTrigger>
                    <TabsTrigger value="sitetypes" className="gap-1">
                      <Building2 className="h-4 w-4" /> {t("settings.masterData.siteTypes")}
                    </TabsTrigger>
                    <TabsTrigger value="zonetypes" className="gap-1">
                      <Layers className="h-4 w-4" /> {t("settings.masterData.zoneTypes")}
                    </TabsTrigger>
                    <TabsTrigger value="areatypes" className="gap-1">
                      <Grid3X3 className="h-4 w-4" /> {t("settings.masterData.areaTypes")}
                    </TabsTrigger>
                    <TabsTrigger value="materialtypes" className="gap-1">
                      <Package className="h-4 w-4" /> {t("settings.masterData.materialTypes")}
                    </TabsTrigger>
                  </TabsList>
                </div>

                <div>
                {/* ACTIVITIES TAB */}
                <TabsContent value="activities">
                  <Tabs value={activitiesSubTab} onValueChange={setActivitiesSubTab} className="mb-4">
                    <div className="flex flex-wrap justify-between items-center gap-2 mb-4">
                      <TabsList>
                        <TabsTrigger value="main">{t("settings.masterData.mainActivities")}</TabsTrigger>
                        <TabsTrigger value="sub">{t("settings.masterData.subActivities")}</TabsTrigger>
                      </TabsList>
                      {activitiesSubTab === "main" ? (
                        <Dialog open={newMainActivityOpen} onOpenChange={setNewMainActivityOpen}>
                          <DialogTrigger asChild>
                            <Button size="sm" className="bg-[#5B2C93] hover:bg-[#5B2C93] gap-2">
                              <Plus className="h-4 w-4" /> {t("settings.masterData.addMainActivity")}
                            </Button>
                          </DialogTrigger>
                          <DialogContent>
                            <form onSubmit={(e) => { e.preventDefault(); createMainActivityMutation.mutate(newMainActivity); }}>
                              <DialogHeader>
                                <DialogTitle>{t("settings.masterData.addNewMainActivity")}</DialogTitle>
                              </DialogHeader>
                              <div className="grid gap-4 py-4">
                                <div className="space-y-2">
                                  <Label>{t("settings.masterData.activityNameEn")}</Label>
                                  <Input placeholder={t("settings.masterData.activityNameEnPlaceholder")} value={newMainActivity.name} onChange={e => setNewMainActivity({...newMainActivity, name: e.target.value})} required />
                                </div>
                                <div className="space-y-2">
                                  <Label>{t("settings.masterData.activityNameAr")}</Label>
                                  <Input placeholder={t("settings.masterData.activityNameArPlaceholder")} value={newMainActivity.nameAr} onChange={e => setNewMainActivity({...newMainActivity, nameAr: e.target.value})} dir="rtl" />
                                </div>
                                <div className="space-y-2">
                                  <Label>{t("common.description")}</Label>
                                  <Input placeholder={t("common.briefDescription")} value={newMainActivity.description} onChange={e => setNewMainActivity({...newMainActivity, description: e.target.value})} />
                                </div>
                              </div>
                              <DialogFooter>
                                <Button type="button" variant="outline" onClick={() => setNewMainActivityOpen(false)}>{t("common.cancel")}</Button>
                                <Button type="submit" className="bg-[#5B2C93] hover:bg-[#5B2C93]" disabled={createMainActivityMutation.isPending}>
                                  {createMainActivityMutation.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                                  {t("common.create")}
                                </Button>
                              </DialogFooter>
                            </form>
                          </DialogContent>
                        </Dialog>
                      ) : (
                        <Dialog open={newSubActivityOpen} onOpenChange={setNewSubActivityOpen}>
                          <DialogTrigger asChild>
                            <Button size="sm" className="bg-[#5B2C93] hover:bg-[#5B2C93] gap-2">
                              <Plus className="h-4 w-4" /> {t("settings.masterData.addSubActivity")}
                            </Button>
                          </DialogTrigger>
                          <DialogContent>
                            <form onSubmit={(e) => { e.preventDefault(); createSubActivityMutation.mutate(newSubActivity); }}>
                              <DialogHeader>
                                <DialogTitle>{t("settings.masterData.addNewSubActivity")}</DialogTitle>
                              </DialogHeader>
                              <div className="grid gap-4 py-4">
                                <div className="space-y-2">
                                  <Label>{t("settings.masterData.mainActivity")}</Label>
                                  <Select value={newSubActivity.mainActivityId.toString()} onValueChange={v => setNewSubActivity({...newSubActivity, mainActivityId: parseInt(v)})}>
                                    <SelectTrigger><SelectValue placeholder={t("settings.masterData.selectMainActivity")} /></SelectTrigger>
                                    <SelectContent>
                                      {mainActivitiesData?.filter(a => a.isActive).map(a => (
                                        <SelectItem key={a.id} value={a.id.toString()}>{a.name}</SelectItem>
                                      ))}
                                    </SelectContent>
                                  </Select>
                                </div>
                                <div className="space-y-2">
                                  <Label>{t("settings.masterData.subActivityNameEn")}</Label>
                                  <Input placeholder={t("settings.masterData.subActivityNameEnPlaceholder")} value={newSubActivity.name} onChange={e => setNewSubActivity({...newSubActivity, name: e.target.value})} required />
                                </div>
                                <div className="space-y-2">
                                  <Label>{t("settings.masterData.subActivityNameAr")}</Label>
                                  <Input placeholder={t("settings.masterData.subActivityNameArPlaceholder")} value={newSubActivity.nameAr} onChange={e => setNewSubActivity({...newSubActivity, nameAr: e.target.value})} dir="rtl" />
                                </div>
                                <div className="space-y-2">
                                  <Label>{t("settings.masterData.riskLevel")}</Label>
                                  <Select value={newSubActivity.riskLevel} onValueChange={v => setNewSubActivity({...newSubActivity, riskLevel: v as any})}>
                                    <SelectTrigger><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                      <SelectItem value="low">{t("settings.masterData.riskLow")}</SelectItem>
                                      <SelectItem value="medium">{t("settings.masterData.riskMedium")}</SelectItem>
                                      <SelectItem value="high">{t("settings.masterData.riskHigh")}</SelectItem>
                                      <SelectItem value="critical">{t("settings.masterData.riskCritical")}</SelectItem>
                                    </SelectContent>
                                  </Select>
                                </div>
                                <div className="flex gap-4">
                                  <div className="flex items-center space-x-2">
                                    <Switch checked={newSubActivity.requiresMOP} onCheckedChange={c => setNewSubActivity({...newSubActivity, requiresMOP: c})} />
                                    <Label>{t("settings.masterData.requiresMOP")}</Label>
                                  </div>
                                  <div className="flex items-center space-x-2">
                                    <Switch checked={newSubActivity.requiresPermit} onCheckedChange={c => setNewSubActivity({...newSubActivity, requiresPermit: c})} />
                                    <Label>{t("settings.masterData.requiresPermit")}</Label>
                                  </div>
                                </div>
                              </div>
                              <DialogFooter>
                                <Button type="button" variant="outline" onClick={() => setNewSubActivityOpen(false)}>{t("common.cancel")}</Button>
                                <Button type="submit" className="bg-[#5B2C93] hover:bg-[#5B2C93]" disabled={createSubActivityMutation.isPending}>
                                  {createSubActivityMutation.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                                  {t("common.create")}
                                </Button>
                              </DialogFooter>
                            </form>
                          </DialogContent>
                        </Dialog>
                      )}
                    </div>

                    <TabsContent value="main">
                      {mainActivitiesLoading ? (
                        <div className="flex items-center justify-center py-8"><Loader2 className="h-6 w-6 animate-spin text-[#5B2C93]" /></div>
                      ) : (
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead>{t("settings.masterData.mainActivityName")}</TableHead>
                              <TableHead>{t("common.description")}</TableHead>
                              <TableHead>{t("settings.masterData.subActivitiesCount")}</TableHead>
                              <TableHead>{t("common.status")}</TableHead>
                              <TableHead className="text-right">{t("common.actions")}</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {mainActivitiesData?.map(activity => {
                              const subCount = subActivitiesData?.filter(s => s.mainActivityId === activity.id && s.isActive).length || 0;
                              return (
                                <TableRow key={activity.id}>
                                  <TableCell className="font-medium">
                                    <div className="flex items-center gap-2">
                                      <Activity className="h-4 w-4 text-[#5B2C93]" />
                                      {activity.name}
                                    </div>
                                  </TableCell>
                                  <TableCell className="text-[#6B6B6B] max-w-md truncate">{activity.description || "-"}</TableCell>
                                  <TableCell>{subCount}</TableCell>
                                  <TableCell>
                                    <Badge variant={activity.isActive ? "default" : "secondary"} className={activity.isActive ? "bg-[#D1FAE5] text-[#059669]" : ""}>
                                      {activity.isActive ? t("common.active") : t("common.inactive")}
                                    </Badge>
                                  </TableCell>
                                  <TableCell className="text-right">
                                    <Button variant="ghost" size="icon" onClick={() => { setEditingMainActivity({...activity}); setEditMainActivityOpen(true); }}>
                                      <Edit2 className="h-4 w-4" />
                                    </Button>
                                    <Button variant="ghost" size="icon" onClick={() => { if(confirm(t("settings.masterData.deleteMainActivityConfirm"))) deleteMainActivityMutation.mutate({ id: activity.id }); }}>
                                      <Trash2 className="h-4 w-4 text-[#FF6B6B]" />
                                    </Button>
                                  </TableCell>
                                </TableRow>
                              );
                            })}
                            {(!mainActivitiesData || mainActivitiesData.length === 0) && (
                              <TableRow>
                                <TableCell colSpan={5} className="text-center text-[#6B6B6B] py-8">{t("settings.masterData.noMainActivities")}</TableCell>
                              </TableRow>
                            )}
                          </TableBody>
                        </Table>
                      )}
                    </TabsContent>

                    <TabsContent value="sub">
                      {subActivitiesLoading ? (
                        <div className="flex items-center justify-center py-8"><Loader2 className="h-6 w-6 animate-spin text-[#5B2C93]" /></div>
                      ) : (
                        <div className="overflow-x-auto">
                          <Table>
                            <TableHeader>
                              <TableRow>
                                <TableHead>{t("settings.masterData.subActivityName")}</TableHead>
                                <TableHead>{t("settings.masterData.mainActivity")}</TableHead>
                                <TableHead className="text-center">RFC</TableHead>
                                <TableHead className="text-center">HRS</TableHead>
                                <TableHead className="text-center">MOP</TableHead>
                                <TableHead className="text-center">MHV</TableHead>
                                <TableHead className="text-center">{t("settings.masterData.room")}</TableHead>
                                <TableHead>{t("common.status")}</TableHead>
                                <TableHead className="text-right">{t("common.actions")}</TableHead>
                              </TableRow>
                            </TableHeader>
                            <TableBody>
                              {subActivitiesData?.map(sub => (
                                <TableRow key={sub.id}>
                                  <TableCell className="font-medium">
                                    <div>
                                      <div>{sub.name}</div>
                                      {sub.nameAr && <div className="text-xs text-[#6B6B6B]" dir="rtl">{sub.nameAr}</div>}
                                    </div>
                                  </TableCell>
                                  <TableCell className="text-[#6B6B6B]">{sub.mainActivityName || "-"}</TableCell>
                                  <TableCell className="text-center">{sub.needsRFC ? <Badge className="bg-[#E8DCF5] text-[#5B2C93] hover:bg-[#E8DCF5]">✓</Badge> : <span className="text-[#6B6B6B]">-</span>}</TableCell>
                                  <TableCell className="text-center">{sub.needsHRS ? <Badge className="bg-[#E8DCF5] text-[#5B2C93] hover:bg-[#E8DCF5]">✓</Badge> : <span className="text-[#6B6B6B]">-</span>}</TableCell>
                                  <TableCell className="text-center">{sub.needsMOP ? <Badge className="bg-[#FEF3C7] text-[#D97706] hover:bg-[#FEF3C7]">✓</Badge> : <span className="text-[#6B6B6B]">-</span>}</TableCell>
                                  <TableCell className="text-center">{sub.needsMHV ? <Badge className="bg-[#D1FAE5] text-[#059669] hover:bg-[#D1FAE5]">✓</Badge> : <span className="text-[#6B6B6B]">-</span>}</TableCell>
                                  <TableCell className="text-center">{sub.needsRoomSelection ? <Badge className="bg-[#E8DCF5] text-[#5B2C93] hover:bg-[#E8DCF5]">✓</Badge> : <span className="text-[#6B6B6B]">-</span>}</TableCell>
                                  <TableCell>
                                    <Badge variant={sub.isActive ? "default" : "secondary"} className={sub.isActive ? "bg-[#D1FAE5] text-[#059669]" : ""}>
                                      {sub.isActive ? t("common.active") : t("common.inactive")}
                                    </Badge>
                                  </TableCell>
                                  <TableCell className="text-right">
                                    <Button variant="ghost" size="icon" onClick={() => { setEditingSubActivity({...sub}); setEditSubActivityOpen(true); }}><Edit2 className="h-4 w-4" /></Button>
                                    <Button variant="ghost" size="icon" onClick={() => { if(confirm(t("settings.masterData.deleteSubActivityConfirm"))) deleteSubActivityMutation.mutate({ id: sub.id }); }}><Trash2 className="h-4 w-4 text-[#FF6B6B]" /></Button>
                                  </TableCell>
                                </TableRow>
                              ))}
                              {(!subActivitiesData || subActivitiesData.length === 0) && (
                                <TableRow>
                                  <TableCell colSpan={9} className="text-center text-[#6B6B6B] py-8">{t("settings.masterData.noSubActivities")}</TableCell>
                                </TableRow>
                              )}
                            </TableBody>
                          </Table>
                        </div>
                      )}
                    </TabsContent>
                  </Tabs>
                </TabsContent>

                {/* ROLE TYPES TAB */}
                <TabsContent value="roletypes">
                  <div className="flex flex-wrap justify-between items-center gap-2 mb-4">
                    <h3 className="text-lg font-medium">{t("settings.masterData.roleTypes")}</h3>
                    <Dialog open={newRoleTypeOpen} onOpenChange={setNewRoleTypeOpen}>
                      <DialogTrigger asChild>
                        <Button size="sm" className="bg-[#5B2C93] hover:bg-[#5B2C93] gap-2">
                          <Plus className="h-4 w-4" /> {t("settings.masterData.addRoleType")}
                        </Button>
                      </DialogTrigger>
                      <DialogContent>
                        <form onSubmit={(e) => { e.preventDefault(); createRoleTypeMutation.mutate(newRoleType); }}>
                          <DialogHeader>
                            <DialogTitle>{t("settings.masterData.addNewRoleType")}</DialogTitle>
                          </DialogHeader>
                          <div className="grid gap-4 py-4">
                            <div className="space-y-2">
                              <Label>{t("settings.masterData.roleTypeNameEn")}</Label>
                              <Input placeholder={t("settings.masterData.roleTypeNameEnPlaceholder")} value={newRoleType.name} onChange={e => setNewRoleType({...newRoleType, name: e.target.value})} required />
                            </div>
                            <div className="space-y-2">
                              <Label>{t("settings.masterData.roleTypeNameAr")}</Label>
                              <Input placeholder={t("settings.masterData.roleTypeNameArPlaceholder")} value={newRoleType.nameAr} onChange={e => setNewRoleType({...newRoleType, nameAr: e.target.value})} dir="rtl" />
                            </div>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                              <div className="space-y-2">
                                <Label>{t("settings.masterData.category")}</Label>
                                <Select value={newRoleType.category} onValueChange={v => setNewRoleType({...newRoleType, category: v as any})}>
                                  <SelectTrigger><SelectValue /></SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="internal">{t("settings.masterData.catInternal")}</SelectItem>
                                    <SelectItem value="external">{t("settings.masterData.catExternal")}</SelectItem>
                                    <SelectItem value="contractor">{t("settings.masterData.catContractor")}</SelectItem>
                                    <SelectItem value="visitor">{t("settings.masterData.catVisitor")}</SelectItem>
                                  </SelectContent>
                                </Select>
                              </div>
                              <div className="space-y-2">
                                <Label>{t("settings.masterData.accessLevel")}</Label>
                                <Select value={newRoleType.accessLevel} onValueChange={v => setNewRoleType({...newRoleType, accessLevel: v as any})}>
                                  <SelectTrigger><SelectValue /></SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="basic">{t("settings.masterData.accessBasic")}</SelectItem>
                                    <SelectItem value="standard">{t("settings.masterData.accessStandard")}</SelectItem>
                                    <SelectItem value="elevated">{t("settings.masterData.accessElevated")}</SelectItem>
                                    <SelectItem value="full">{t("settings.masterData.accessFull")}</SelectItem>
                                  </SelectContent>
                                </Select>
                              </div>
                            </div>
                            <div className="space-y-2">
                              <Label>{t("common.description")}</Label>
                              <Input value={newRoleType.description} onChange={e => setNewRoleType({...newRoleType, description: e.target.value})} />
                            </div>
                          </div>
                          <DialogFooter>
                            <Button type="button" variant="outline" onClick={() => setNewRoleTypeOpen(false)}>{t("common.cancel")}</Button>
                            <Button type="submit" className="bg-[#5B2C93] hover:bg-[#5B2C93]" disabled={createRoleTypeMutation.isPending}>
                              {createRoleTypeMutation.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                              {t("common.create")}
                            </Button>
                          </DialogFooter>
                        </form>
                      </DialogContent>
                    </Dialog>
                  </div>
                  {roleTypesLoading ? (
                    <div className="flex items-center justify-center py-8"><Loader2 className="h-6 w-6 animate-spin text-[#5B2C93]" /></div>
                  ) : (
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>{t("settings.masterData.roleTypeName")}</TableHead>
                          <TableHead>{t("settings.masterData.category")}</TableHead>
                          <TableHead>{t("settings.masterData.accessLevel")}</TableHead>
                          <TableHead>{t("common.status")}</TableHead>
                          <TableHead className="text-right">{t("common.actions")}</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {(roleTypesData || []).map(rt => (
                          <TableRow key={rt.id}>
                            <TableCell className="font-medium">
                              <div>{rt.name}</div>
                              {rt.nameAr && <div className="text-xs text-[#6B6B6B]" dir="rtl">{rt.nameAr}</div>}
                            </TableCell>
                            <TableCell><Badge variant="outline">{rt.category}</Badge></TableCell>
                            <TableCell><Badge variant="secondary">{rt.accessLevel}</Badge></TableCell>
                            <TableCell>
                              <Badge variant={rt.isActive ? "default" : "secondary"} className={rt.isActive ? "bg-[#D1FAE5] text-[#059669]" : ""}>
                                {rt.isActive ? t("common.active") : t("common.inactive")}
                              </Badge>
                            </TableCell>
                            <TableCell className="text-right">
                              <Button variant="ghost" size="icon" onClick={() => { setEditingRoleType({...rt}); setEditRoleTypeOpen(true); }}><Edit2 className="h-4 w-4" /></Button>
                              <Button variant="ghost" size="icon" onClick={() => { if(confirm(t("settings.masterData.deleteRoleTypeConfirm"))) deleteRoleTypeMutation.mutate({ id: rt.id }); }}><Trash2 className="h-4 w-4 text-[#FF6B6B]" /></Button>
                            </TableCell>
                          </TableRow>
                        ))}
                        {(!roleTypesData || roleTypesData.length === 0) && (
                          <TableRow>
                            <TableCell colSpan={5} className="text-center text-[#6B6B6B] py-8">{t("settings.masterData.noRoleTypes")}</TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  )}
                </TabsContent>

                {/* APPROVERS TAB */}
                <TabsContent value="approvers">
                  <div className="flex flex-wrap justify-between items-center gap-2 mb-4">
                    <h3 className="text-lg font-medium">{t("settings.masterData.approvers")}</h3>
                    <Dialog open={newApproverOpen} onOpenChange={setNewApproverOpen}>
                      <DialogTrigger asChild>
                        <Button size="sm" className="bg-[#5B2C93] hover:bg-[#5B2C93] gap-2">
                          <Plus className="h-4 w-4" /> {t("settings.masterData.addApprover")}
                        </Button>
                      </DialogTrigger>
                      <DialogContent>
                        <form onSubmit={(e) => { e.preventDefault(); createApproverMutation.mutate(newApprover); }}>
                          <DialogHeader>
                            <DialogTitle>{t("settings.masterData.addNewApprover")}</DialogTitle>
                          </DialogHeader>
                          <div className="grid gap-4 py-4">
                            <div className="space-y-2">
                              <Label>{t("common.user")}</Label>
                              <Select value={newApprover.userId.toString()} onValueChange={v => setNewApprover({...newApprover, userId: parseInt(v)})}>
                                <SelectTrigger><SelectValue placeholder={t("settings.masterData.selectUser")} /></SelectTrigger>
                                <SelectContent>
                                  {usersForApprovers?.users?.map(u => (
                                    <SelectItem key={u.id} value={u.id.toString()}>{u.name || u.email}</SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </div>
                            <div className="space-y-2">
                              <Label>{t("settings.masterData.siteOptional")}</Label>
                              <Select value={newApprover.siteId?.toString() || "all"} onValueChange={v => setNewApprover({...newApprover, siteId: v === "all" ? undefined : parseInt(v)})}>
                                <SelectTrigger><SelectValue /></SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="all">{t("settings.masterData.allSites")}</SelectItem>
                                  {sitesForApprovers?.map(s => (
                                    <SelectItem key={s.id} value={s.id.toString()}>{s.name}</SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </div>
                            <div className="space-y-2">
                              <Label>{t("settings.masterData.approvalLevel")}</Label>
                              <Input type="number" min={1} max={10} value={newApprover.approvalLevel} onChange={e => setNewApprover({...newApprover, approvalLevel: parseInt(e.target.value) || 1})} />
                            </div>
                            <div className="flex gap-4">
                              <div className="flex items-center space-x-2">
                                <Switch checked={newApprover.canApproveEmergency} onCheckedChange={c => setNewApprover({...newApprover, canApproveEmergency: c})} />
                                <Label>{t("settings.masterData.canApproveEmergency")}</Label>
                              </div>
                              <div className="flex items-center space-x-2">
                                <Switch checked={newApprover.canApproveVIP} onCheckedChange={c => setNewApprover({...newApprover, canApproveVIP: c})} />
                                <Label>{t("settings.masterData.canApproveVIP")}</Label>
                              </div>
                            </div>
                          </div>
                          <DialogFooter>
                            <Button type="button" variant="outline" onClick={() => setNewApproverOpen(false)}>{t("common.cancel")}</Button>
                            <Button type="submit" className="bg-[#5B2C93] hover:bg-[#5B2C93]" disabled={createApproverMutation.isPending}>
                              {createApproverMutation.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                              {t("common.create")}
                            </Button>
                          </DialogFooter>
                        </form>
                      </DialogContent>
                    </Dialog>
                  </div>
                  {approversLoading ? (
                    <div className="flex items-center justify-center py-8"><Loader2 className="h-6 w-6 animate-spin text-[#5B2C93]" /></div>
                  ) : (
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>{t("common.user")}</TableHead>
                          <TableHead>{t("common.site")}</TableHead>
                          <TableHead>{t("settings.masterData.level")}</TableHead>
                          <TableHead>{t("settings.masterData.capabilities")}</TableHead>
                          <TableHead>{t("common.status")}</TableHead>
                          <TableHead className="text-right">{t("common.actions")}</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {(approversData || []).map(approver => (
                          <TableRow key={approver.id}>
                            <TableCell>
                              <div className="flex items-center gap-2">
                                <div>
                                  <div className="font-medium">{approver.userName}</div>
                                  <div className="text-xs text-[#6B6B6B]">{approver.userEmail}</div>
                                </div>
                              </div>
                            </TableCell>
                            <TableCell>{approver.siteName || t("settings.masterData.allSites")}</TableCell>
                            <TableCell><Badge variant="outline">{t("settings.masterData.levelLabel")} {approver.approvalLevel}</Badge></TableCell>
                            <TableCell>
                              <div className="flex gap-1">
                                {approver.canApproveEmergency && <Badge variant="destructive" className="text-xs">{t("settings.masterData.emergency")}</Badge>}
                                {approver.canApproveVIP && <Badge variant="secondary" className="text-xs">VIP</Badge>}
                                {!approver.canApproveEmergency && !approver.canApproveVIP && <span className="text-[#6B6B6B]">{t("settings.masterData.standard")}</span>}
                              </div>
                            </TableCell>
                            <TableCell>
                              <Badge variant={approver.isActive ? "default" : "secondary"} className={approver.isActive ? "bg-[#D1FAE5] text-[#059669]" : ""}>
                                {approver.isActive ? t("common.active") : t("common.inactive")}
                              </Badge>
                            </TableCell>
                            <TableCell className="text-right">
                              <Button variant="ghost" size="icon" onClick={() => { setEditingApprover({...approver}); setEditApproverOpen(true); }}><Edit2 className="h-4 w-4" /></Button>
                              <Button variant="ghost" size="icon" onClick={() => { if(confirm(t("settings.masterData.deleteApproverConfirm"))) deleteApproverMutation.mutate({ id: approver.id }); }}><Trash2 className="h-4 w-4 text-[#FF6B6B]" /></Button>
                            </TableCell>
                          </TableRow>
                        ))}
                        {(!approversData || approversData.length === 0) && (
                          <TableRow>
                            <TableCell colSpan={6} className="text-center text-[#6B6B6B] py-8">{t("settings.masterData.noApprovers")}</TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  )}
                </TabsContent>

                {/* LOCATION TAB - Countries, Regions, Cities, Sites, Zones, Areas */}
                <TabsContent value="location">
                  <Tabs value={locationSubTab} onValueChange={setLocationSubTab} className="mb-4">
                    <div className="border-b border-[#E0E0E0] mb-4">
                      <TabsList className="flex gap-1 h-auto p-0 bg-transparent flex-nowrap justify-start">
                        <TabsTrigger value="countries">{t("settings.masterData.countries")}</TabsTrigger>
                        <TabsTrigger value="regions">{t("settings.masterData.regions")}</TabsTrigger>
                        <TabsTrigger value="cities">{t("settings.masterData.cities")}</TabsTrigger>
                        <TabsTrigger value="sites">Sites</TabsTrigger>
                        <TabsTrigger value="zones">Zones</TabsTrigger>
                        <TabsTrigger value="areas">Areas</TabsTrigger>
                      </TabsList>
                    </div>

                    {/* COUNTRIES SUB-TAB */}
                    <TabsContent value="countries">
                  <div className="flex flex-wrap justify-between items-center gap-2 mb-4">
                    <h3 className="text-lg font-medium">{t("settings.masterData.countries")}</h3>
                    <Dialog open={newCountryOpen} onOpenChange={setNewCountryOpen}>
                      <DialogTrigger asChild>
                        <Button size="sm" className="bg-[#5B2C93] hover:bg-[#5B2C93] gap-2">
                          <Plus className="h-4 w-4" /> {t("settings.masterData.addCountry")}
                        </Button>
                      </DialogTrigger>
                      <DialogContent>
                        <form onSubmit={(e) => { e.preventDefault(); createCountryMutation.mutate(newCountry); }}>
                          <DialogHeader><DialogTitle>{t("settings.masterData.addNewCountry")}</DialogTitle></DialogHeader>
                          <div className="grid gap-4 py-4">
                            <div className="space-y-2">
                              <Label>{t("settings.masterData.countryCode")}</Label>
                              <Input placeholder={t("settings.masterData.countryCodePlaceholder")} maxLength={3} value={newCountry.code} onChange={e => setNewCountry({...newCountry, code: e.target.value.toUpperCase()})} required />
                            </div>
                            <div className="space-y-2">
                              <Label>{t("settings.masterData.countryName")}</Label>
                              <Input placeholder={t("settings.masterData.countryNamePlaceholder")} value={newCountry.name} onChange={e => setNewCountry({...newCountry, name: e.target.value})} required />
                            </div>
                          </div>
                          <DialogFooter>
                            <Button type="button" variant="outline" onClick={() => setNewCountryOpen(false)}>{t("common.cancel")}</Button>
                            <Button type="submit" className="bg-[#5B2C93] hover:bg-[#5B2C93]" disabled={createCountryMutation.isPending}>
                              {createCountryMutation.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                              {t("common.create")}
                            </Button>
                          </DialogFooter>
                        </form>
                      </DialogContent>
                    </Dialog>
                  </div>
                  {countriesLoading ? (
                    <div className="flex items-center justify-center py-8"><Loader2 className="h-6 w-6 animate-spin text-[#5B2C93]" /></div>
                  ) : (
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>{t("settings.masterData.code")}</TableHead>
                          <TableHead>{t("settings.masterData.name")}</TableHead>
                          <TableHead>{t("common.status")}</TableHead>
                          <TableHead className="text-right">{t("common.actions")}</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {(countriesData || []).map((country) => (
                          <TableRow key={country.id}>
                            <TableCell className="font-mono">{country.code}</TableCell>
                            <TableCell>{country.name}</TableCell>
                            <TableCell>
                              <Badge variant={country.isActive ? "default" : "secondary"}>
                                {country.isActive ? t("common.active") : t("common.inactive")}
                              </Badge>
                            </TableCell>
                            <TableCell className="text-right">
                              <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => { setEditingCountry(country); setEditCountryOpen(true); }}><Edit2 className="h-4 w-4" /></Button>
                              <Button variant="ghost" size="icon" className="h-8 w-8 hover:text-[#FF6B6B]" onClick={() => { if(confirm(t("settings.masterData.deleteCountryConfirm"))) deleteCountryMutation.mutate({ id: country.id }); }}><Trash2 className="h-4 w-4" /></Button>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  )}
                    </TabsContent>

                    {/* REGIONS SUB-TAB */}
                    <TabsContent value="regions">
                  <div className="flex flex-wrap justify-between items-center gap-2 mb-4">
                    <h3 className="text-lg font-medium">{t("settings.masterData.regions")}</h3>
                    <Dialog open={newRegionOpen} onOpenChange={setNewRegionOpen}>
                      <DialogTrigger asChild>
                        <Button size="sm" className="bg-[#5B2C93] hover:bg-[#5B2C93] gap-2">
                          <Plus className="h-4 w-4" /> {t("settings.masterData.addRegion")}
                        </Button>
                      </DialogTrigger>
                      <DialogContent>
                        <form onSubmit={(e) => { e.preventDefault(); createRegionMutation.mutate(newRegion); }}>
                          <DialogHeader><DialogTitle>{t("settings.masterData.addNewRegion")}</DialogTitle></DialogHeader>
                          <div className="grid gap-4 py-4">
                            <div className="space-y-2">
                              <Label>{t("settings.masterData.regionCode")}</Label>
                              <Input placeholder={t("settings.masterData.regionCodePlaceholder")} value={newRegion.code} onChange={e => setNewRegion({...newRegion, code: e.target.value.toUpperCase()})} required />
                            </div>
                            <div className="space-y-2">
                              <Label>{t("settings.masterData.regionName")}</Label>
                              <Input placeholder={t("settings.masterData.regionNamePlaceholder")} value={newRegion.name} onChange={e => setNewRegion({...newRegion, name: e.target.value})} required />
                            </div>
                          </div>
                          <DialogFooter>
                            <Button type="button" variant="outline" onClick={() => setNewRegionOpen(false)}>{t("common.cancel")}</Button>
                            <Button type="submit" className="bg-[#5B2C93] hover:bg-[#5B2C93]" disabled={createRegionMutation.isPending}>
                              {createRegionMutation.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                              {t("common.create")}
                            </Button>
                          </DialogFooter>
                        </form>
                      </DialogContent>
                    </Dialog>
                  </div>
                  {regionsLoading ? (
                    <div className="flex items-center justify-center py-8"><Loader2 className="h-6 w-6 animate-spin text-[#5B2C93]" /></div>
                  ) : (
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>{t("settings.masterData.code")}</TableHead>
                          <TableHead>{t("settings.masterData.name")}</TableHead>
                          <TableHead>{t("common.status")}</TableHead>
                          <TableHead className="text-right">{t("common.actions")}</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {(regionsData || []).map((region) => (
                          <TableRow key={region.id}>
                            <TableCell className="font-mono">{region.code}</TableCell>
                            <TableCell>{region.name}</TableCell>
                            <TableCell>
                              <Badge variant={region.isActive ? "default" : "secondary"}>
                                {region.isActive ? t("common.active") : t("common.inactive")}
                              </Badge>
                            </TableCell>
                            <TableCell className="text-right">
                              <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => { setEditingRegion(region); setEditRegionOpen(true); }}><Edit2 className="h-4 w-4" /></Button>
                              <Button variant="ghost" size="icon" className="h-8 w-8 hover:text-[#FF6B6B]" onClick={() => { if(confirm(t("settings.masterData.deleteRegionConfirm"))) deleteRegionMutation.mutate({ id: region.id }); }}><Trash2 className="h-4 w-4" /></Button>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  )}
                    </TabsContent>

                    {/* CITIES SUB-TAB */}
                    <TabsContent value="cities">
                  <div className="flex flex-wrap justify-between items-center gap-2 mb-4">
                    <h3 className="text-lg font-medium">{t("settings.masterData.cities")}</h3>
                    <Dialog open={newCityOpen} onOpenChange={setNewCityOpen}>
                      <DialogTrigger asChild>
                        <Button size="sm" className="bg-[#5B2C93] hover:bg-[#5B2C93] gap-2">
                          <Plus className="h-4 w-4" /> {t("settings.masterData.addCity")}
                        </Button>
                      </DialogTrigger>
                      <DialogContent>
                        <form onSubmit={(e) => { e.preventDefault(); if(newCity.countryId) createCityMutation.mutate(newCity); }}>
                          <DialogHeader><DialogTitle>{t("settings.masterData.addNewCity")}</DialogTitle></DialogHeader>
                          <div className="grid gap-4 py-4">
                            <div className="space-y-2">
                              <Label>{t("settings.masterData.country")}</Label>
                              <Select value={newCity.countryId?.toString() || ""} onValueChange={(v) => setNewCity({...newCity, countryId: parseInt(v)})}>
                                <SelectTrigger><SelectValue placeholder={t("settings.masterData.selectCountry")} /></SelectTrigger>
                                <SelectContent>
                                  {(countriesData || []).filter(c => c.isActive).map(c => (
                                    <SelectItem key={c.id} value={c.id.toString()}>{c.name}</SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </div>
                            <div className="space-y-2">
                              <Label>{t("settings.masterData.cityName")}</Label>
                              <Input placeholder={t("settings.masterData.cityNamePlaceholder")} value={newCity.name} onChange={e => setNewCity({...newCity, name: e.target.value})} required />
                            </div>
                          </div>
                          <DialogFooter>
                            <Button type="button" variant="outline" onClick={() => setNewCityOpen(false)}>{t("common.cancel")}</Button>
                            <Button type="submit" className="bg-[#5B2C93] hover:bg-[#5B2C93]" disabled={createCityMutation.isPending || !newCity.countryId}>
                              {createCityMutation.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                              {t("common.create")}
                            </Button>
                          </DialogFooter>
                        </form>
                      </DialogContent>
                    </Dialog>
                  </div>
                  {citiesLoading ? (
                    <div className="flex items-center justify-center py-8"><Loader2 className="h-6 w-6 animate-spin text-[#5B2C93]" /></div>
                  ) : (
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>{t("settings.masterData.cityName")}</TableHead>
                          <TableHead>{t("settings.masterData.country")}</TableHead>
                          <TableHead>{t("common.status")}</TableHead>
                          <TableHead className="text-right">{t("common.actions")}</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {(citiesData || []).map((city) => (
                          <TableRow key={city.id}>
                            <TableCell>{city.name}</TableCell>
                            <TableCell>{city.countryName || "—"}</TableCell>
                            <TableCell>
                              <Badge variant={city.isActive ? "default" : "secondary"}>
                                {city.isActive ? t("common.active") : t("common.inactive")}
                              </Badge>
                            </TableCell>
                            <TableCell className="text-right">
                              <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => { setEditingCity(city); setEditCityOpen(true); }}><Edit2 className="h-4 w-4" /></Button>
                              <Button variant="ghost" size="icon" className="h-8 w-8 hover:text-[#FF6B6B]" onClick={() => { if(confirm(t("settings.masterData.deleteCityConfirm"))) deleteCityMutation.mutate({ id: city.id }); }}><Trash2 className="h-4 w-4" /></Button>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  )}
                    </TabsContent>

                    {/* SITES SUB-TAB */}
                    <TabsContent value="sites">
                      <SitesPage />
                    </TabsContent>

                    {/* ZONES SUB-TAB */}
                    <TabsContent value="zones">
                      <ZonesPage />
                    </TabsContent>

                    {/* AREAS SUB-TAB */}
                    <TabsContent value="areas">
                      <AreasPage />
                    </TabsContent>
                  </Tabs>
                </TabsContent>

                {/* SITE / ZONE / AREA TYPES TABS */}
                <TabsContent value="sitetypes">
                  <HierarchicalTypeManager
                    title={t("settings.masterData.siteTypes")}
                    data={(siteTypesData || []).map(t2 => ({ ...t2, parentId: t2.parentId ?? null, level: t2.level ?? 0, sortOrder: t2.sortOrder ?? 0, nameAr: t2.nameAr ?? null }))}
                    isLoading={siteTypesLoading}
                    onCreate={(data) => createSiteTypeMutation.mutate(data)}
                    onUpdate={(data) => updateSiteTypeMutation.mutate(data)}
                    onDelete={(id) => deleteSiteTypeMutation.mutate({ id })}
                    isCreating={createSiteTypeMutation.isPending}
                    isUpdating={updateSiteTypeMutation.isPending}
                    isDeleting={deleteSiteTypeMutation.isPending}
                  />
                </TabsContent>
                <TabsContent value="zonetypes">
                  <HierarchicalTypeManager
                    title={t("settings.masterData.zoneTypes")}
                    data={(zoneTypesData || []).map(t2 => ({ ...t2, parentId: t2.parentId ?? null, level: t2.level ?? 0, sortOrder: t2.sortOrder ?? 0, nameAr: t2.nameAr ?? null }))}
                    isLoading={zoneTypesLoading}
                    onCreate={(data) => createZoneTypeMutation.mutate(data)}
                    onUpdate={(data) => updateZoneTypeMutation.mutate(data)}
                    onDelete={(id) => deleteZoneTypeMutation.mutate({ id })}
                    isCreating={createZoneTypeMutation.isPending}
                    isUpdating={updateZoneTypeMutation.isPending}
                    isDeleting={deleteZoneTypeMutation.isPending}
                  />
                </TabsContent>
                <TabsContent value="areatypes">
                  <HierarchicalTypeManager
                    title={t("settings.masterData.areaTypes")}
                    data={(areaTypesData || []).map(t2 => ({ ...t2, parentId: t2.parentId ?? null, level: t2.level ?? 0, sortOrder: t2.sortOrder ?? 0, nameAr: t2.nameAr ?? null }))}
                    isLoading={areaTypesLoading}
                    onCreate={(data) => createAreaTypeMutation.mutate(data)}
                    onUpdate={(data) => updateAreaTypeMutation.mutate(data)}
                    onDelete={(id) => deleteAreaTypeMutation.mutate({ id })}
                    isCreating={createAreaTypeMutation.isPending}
                    isUpdating={updateAreaTypeMutation.isPending}
                    isDeleting={deleteAreaTypeMutation.isPending}
                  />
                </TabsContent>
                <TabsContent value="materialtypes">
                  <MaterialTypesTab />
                </TabsContent>

                {/* COMPANIES TAB */}
                <TabsContent value="companies">
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <h3 className="text-lg font-medium">{t("settings.masterData.companies")}</h3>
                        <div className="flex gap-1">
                          <Button size="sm" variant={companiesFilter === "all" ? "default" : "outline"} onClick={() => setCompaniesFilter("all")}>{t("common.all")}</Button>
                          <Button size="sm" variant={companiesFilter === "contractor" ? "default" : "outline"} onClick={() => setCompaniesFilter("contractor")}>{t("settings.masterData.contractors")}</Button>
                          <Button size="sm" variant={companiesFilter === "subcontractor" ? "default" : "outline"} onClick={() => setCompaniesFilter("subcontractor")}>{t("settings.masterData.subContractors")}</Button>
                          <Button size="sm" variant={companiesFilter === "client" ? "default" : "outline"} onClick={() => setCompaniesFilter("client")}>{t("settings.masterData.clients")}</Button>
                        </div>
                      </div>
                      <Button onClick={() => setNewCompanyOpen(true)}>
                        <Plus className="h-4 w-4 mr-2" /> {t("settings.masterData.addCompany")}
                      </Button>
                    </div>
                    <p className="text-sm text-[#6B6B6B]">{t("settings.masterData.companiesDesc")}</p>
                    {companiesLoading ? (
                      <div className="flex items-center justify-center py-8"><Loader2 className="h-6 w-6 animate-spin" /></div>
                    ) : (
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>{t("settings.masterData.code")}</TableHead>
                            <TableHead>{t("settings.masterData.companyName")}</TableHead>
                            <TableHead>{t("common.type")}</TableHead>
                            <TableHead>{t("settings.masterData.contractRef")}</TableHead>
                            <TableHead>{t("settings.masterData.contractPeriod")}</TableHead>
                            <TableHead>{t("settings.masterData.contactPerson")}</TableHead>
                            <TableHead>{t("common.status")}</TableHead>
                            <TableHead className="w-[100px]">{t("common.actions")}</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {filteredCompanies.map((company) => (
                            <TableRow key={company.id}>
                              <TableCell className="font-mono text-sm">{company.code}</TableCell>
                              <TableCell>
                                <div>
                                  <div className="font-medium">{company.name}</div>
                                  {company.nameAr && <div className="text-sm text-[#6B6B6B]" dir="rtl">{company.nameAr}</div>}
                                </div>
                              </TableCell>
                              <TableCell>
                                <Badge variant={company.type === "contractor" ? "default" : company.type === "subcontractor" ? "secondary" : "outline"}>
                                  {company.type === "contractor" ? t("settings.masterData.contractor") : company.type === "subcontractor" ? t("settings.masterData.subContractor") : t("settings.masterData.client")}
                                </Badge>
                              </TableCell>
                              <TableCell>{company.contractReference || "-"}</TableCell>
                              <TableCell>
                                {company.contractStartDate && company.contractEndDate ? (
                                  <span className="text-sm">
                                    {new Date(company.contractStartDate).toLocaleDateString()} - {new Date(company.contractEndDate).toLocaleDateString()}
                                  </span>
                                ) : "-"}
                              </TableCell>
                              <TableCell>
                                {company.contactPersonName ? (
                                  <div className="text-sm">
                                    <div>{company.contactPersonName}</div>
                                    {company.contactPersonEmail && <div className="text-[#6B6B6B]">{company.contactPersonEmail}</div>}
                                  </div>
                                ) : "-"}
                              </TableCell>
                              <TableCell>
                                <Badge variant={company.status === "active" ? "default" : "secondary"}>
                                  {company.status === "active" ? t("common.active") : t("common.inactive")}
                                </Badge>
                              </TableCell>
                              <TableCell>
                                <Button variant="ghost" size="icon" onClick={() => { setEditingCompany(company); setEditCompanyOpen(true); }}><Edit2 className="h-4 w-4" /></Button>
                                <Button variant="ghost" size="icon" onClick={() => { if(confirm(t("settings.masterData.deactivateCompanyConfirm"))) deleteCompanyMutation.mutate({ id: company.id }); }}><Trash2 className="h-4 w-4 text-[#FF6B6B]" /></Button>
                              </TableCell>
                            </TableRow>
                          ))}
                          {filteredCompanies.length === 0 && (
                            <TableRow>
                              <TableCell colSpan={8} className="text-center text-[#6B6B6B] py-8">{t("settings.masterData.noCompanies")}</TableCell>
                            </TableRow>
                          )}
                        </TableBody>
                      </Table>
                    )}
                  </div>
                </TabsContent>
                </div>
              </Tabs>
            </CardContent>
          </Card>

          {/* Add Company Dialog */}
          <Dialog open={newCompanyOpen} onOpenChange={setNewCompanyOpen}>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
              <form onSubmit={(e) => { e.preventDefault(); createCompanyMutation.mutate(newCompany); }}>
                <DialogHeader><DialogTitle>{t("settings.masterData.addNewCompany")}</DialogTitle></DialogHeader>
                <div className="grid gap-4 py-4">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>{t("settings.masterData.companyCode")} *</Label>
                      <Input value={newCompany.code} onChange={e => setNewCompany({...newCompany, code: e.target.value})} placeholder={t("settings.masterData.companyCodePlaceholder")} required />
                    </div>
                    <div className="space-y-2">
                      <Label>{t("settings.masterData.companyType")} *</Label>
                      <Select value={newCompany.type} onValueChange={(v: "contractor" | "subcontractor" | "client") => setNewCompany({...newCompany, type: v})}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="contractor">{t("settings.masterData.contractor")}</SelectItem>
                          <SelectItem value="subcontractor">{t("settings.masterData.subContractor")}</SelectItem>
                          <SelectItem value="client">{t("settings.masterData.client")}</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>{t("settings.masterData.companyNameEn")} *</Label>
                      <Input value={newCompany.name} onChange={e => setNewCompany({...newCompany, name: e.target.value})} required />
                    </div>
                    <div className="space-y-2">
                      <Label>{t("settings.masterData.companyNameAr")}</Label>
                      <Input value={newCompany.nameAr} onChange={e => setNewCompany({...newCompany, nameAr: e.target.value})} dir="rtl" />
                    </div>
                  </div>
                  {newCompany.type === "subcontractor" && (
                    <div className="space-y-2">
                      <Label>{t("settings.masterData.parentCompany")}</Label>
                      <Select value={newCompany.parentCompanyId?.toString() || ""} onValueChange={(v) => setNewCompany({...newCompany, parentCompanyId: v ? parseInt(v) : undefined})}>
                        <SelectTrigger><SelectValue placeholder={t("settings.masterData.selectParentCompany")} /></SelectTrigger>
                        <SelectContent>
                          {companiesData?.filter(c => c.type === "contractor").map(c => (
                            <SelectItem key={c.id} value={c.id.toString()}>{c.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                  <div className="border-t pt-4">
                    <h4 className="font-medium mb-3">{t("settings.masterData.contractInfo")}</h4>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                      <div className="space-y-2"><Label>{t("settings.masterData.contractRef")}</Label><Input value={newCompany.contractReference} onChange={e => setNewCompany({...newCompany, contractReference: e.target.value})} placeholder={t("settings.masterData.contractRefPlaceholder")} /></div>
                      <div className="space-y-2"><Label>{t("settings.masterData.startDate")}</Label><Input type="date" value={newCompany.contractStartDate} onChange={e => setNewCompany({...newCompany, contractStartDate: e.target.value})} /></div>
                      <div className="space-y-2"><Label>{t("settings.masterData.endDate")}</Label><Input type="date" value={newCompany.contractEndDate} onChange={e => setNewCompany({...newCompany, contractEndDate: e.target.value})} /></div>
                    </div>
                  </div>
                  <div className="border-t pt-4">
                    <h4 className="font-medium mb-3">{t("settings.masterData.contactPersonSection")}</h4>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div className="space-y-2"><Label>{t("common.name")}</Label><Input value={newCompany.contactPersonName} onChange={e => setNewCompany({...newCompany, contactPersonName: e.target.value})} /></div>
                      <div className="space-y-2"><Label>{t("settings.masterData.position")}</Label><Input value={newCompany.contactPersonPosition} onChange={e => setNewCompany({...newCompany, contactPersonPosition: e.target.value})} /></div>
                      <div className="space-y-2"><Label>{t("common.email")}</Label><Input type="email" value={newCompany.contactPersonEmail} onChange={e => setNewCompany({...newCompany, contactPersonEmail: e.target.value})} /></div>
                      <div className="space-y-2"><Label>{t("common.phone")}</Label><Input value={newCompany.contactPersonPhone} onChange={e => setNewCompany({...newCompany, contactPersonPhone: e.target.value})} /></div>
                    </div>
                  </div>
                  <div className="border-t pt-4">
                    <h4 className="font-medium mb-3">{t("settings.masterData.additionalInfo")}</h4>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div className="space-y-2"><Label>{t("settings.masterData.registrationNumber")}</Label><Input value={newCompany.registrationNumber} onChange={e => setNewCompany({...newCompany, registrationNumber: e.target.value})} /></div>
                      <div className="space-y-2">
                        <Label>{t("common.status")}</Label>
                        <Select value={newCompany.status} onValueChange={(v: "active" | "inactive" | "suspended") => setNewCompany({...newCompany, status: v})}>
                          <SelectTrigger><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="active">{t("common.active")}</SelectItem>
                            <SelectItem value="inactive">{t("common.inactive")}</SelectItem>
                            <SelectItem value="suspended">{t("settings.masterData.suspended")}</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    <div className="space-y-2 mt-4"><Label>{t("settings.masterData.address")}</Label><Input value={newCompany.address} onChange={e => setNewCompany({...newCompany, address: e.target.value})} /></div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4">
                      <div className="space-y-2"><Label>{t("settings.masterData.city")}</Label><Input value={newCompany.city} onChange={e => setNewCompany({...newCompany, city: e.target.value})} /></div>
                      <div className="space-y-2"><Label>{t("settings.masterData.country")}</Label><Input value={newCompany.country} onChange={e => setNewCompany({...newCompany, country: e.target.value})} /></div>
                    </div>
                    <div className="space-y-2 mt-4"><Label>{t("common.notes")}</Label><Input value={newCompany.notes} onChange={e => setNewCompany({...newCompany, notes: e.target.value})} /></div>
                  </div>
                </div>
                <DialogFooter>
                  <Button type="button" variant="outline" onClick={() => setNewCompanyOpen(false)}>{t("common.cancel")}</Button>
                  <Button type="submit" disabled={createCompanyMutation.isPending}>
                    {createCompanyMutation.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                    {t("settings.masterData.createCompany")}
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>

          {/* Edit Company Dialog */}
          <Dialog open={editCompanyOpen} onOpenChange={setEditCompanyOpen}>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
              <form onSubmit={(e) => { e.preventDefault(); if(editingCompany) updateCompanyMutation.mutate(editingCompany); }}>
                <DialogHeader><DialogTitle>{t("settings.masterData.editCompany")}</DialogTitle></DialogHeader>
                {editingCompany && (
                  <div className="grid gap-4 py-4">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div className="space-y-2"><Label>{t("settings.masterData.companyCode")} *</Label><Input value={editingCompany.code} onChange={e => setEditingCompany({...editingCompany, code: e.target.value})} required /></div>
                      <div className="space-y-2">
                        <Label>{t("settings.masterData.companyType")} *</Label>
                        <Select value={editingCompany.type} onValueChange={(v) => setEditingCompany({...editingCompany, type: v})}>
                          <SelectTrigger><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="contractor">{t("settings.masterData.contractor")}</SelectItem>
                            <SelectItem value="subcontractor">{t("settings.masterData.subContractor")}</SelectItem>
                            <SelectItem value="client">{t("settings.masterData.client")}</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div className="space-y-2"><Label>{t("settings.masterData.companyNameEn")} *</Label><Input value={editingCompany.name} onChange={e => setEditingCompany({...editingCompany, name: e.target.value})} required /></div>
                      <div className="space-y-2"><Label>{t("settings.masterData.companyNameAr")}</Label><Input value={editingCompany.nameAr || ""} onChange={e => setEditingCompany({...editingCompany, nameAr: e.target.value})} dir="rtl" /></div>
                    </div>
                    {editingCompany.type === "subcontractor" && (
                      <div className="space-y-2">
                        <Label>{t("settings.masterData.parentCompany")}</Label>
                        <Select value={editingCompany.parentCompanyId?.toString() || ""} onValueChange={(v) => setEditingCompany({...editingCompany, parentCompanyId: v ? parseInt(v) : null})}>
                          <SelectTrigger><SelectValue placeholder={t("settings.masterData.selectParentCompany")} /></SelectTrigger>
                          <SelectContent>
                            {companiesData?.filter(c => c.type === "contractor" && c.id !== editingCompany.id).map(c => (
                              <SelectItem key={c.id} value={c.id.toString()}>{c.name}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    )}
                    <div className="border-t pt-4">
                      <h4 className="font-medium mb-3">{t("settings.masterData.contractInfo")}</h4>
                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                        <div className="space-y-2"><Label>{t("settings.masterData.contractRef")}</Label><Input value={editingCompany.contractReference || ""} onChange={e => setEditingCompany({...editingCompany, contractReference: e.target.value})} /></div>
                        <div className="space-y-2"><Label>{t("settings.masterData.startDate")}</Label><Input type="date" value={editingCompany.contractStartDate ? new Date(editingCompany.contractStartDate).toISOString().split('T')[0] : ""} onChange={e => setEditingCompany({...editingCompany, contractStartDate: e.target.value})} /></div>
                        <div className="space-y-2"><Label>{t("settings.masterData.endDate")}</Label><Input type="date" value={editingCompany.contractEndDate ? new Date(editingCompany.contractEndDate).toISOString().split('T')[0] : ""} onChange={e => setEditingCompany({...editingCompany, contractEndDate: e.target.value})} /></div>
                      </div>
                    </div>
                    <div className="border-t pt-4">
                      <h4 className="font-medium mb-3">{t("settings.masterData.contactPersonSection")}</h4>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div className="space-y-2"><Label>{t("common.name")}</Label><Input value={editingCompany.contactPersonName || ""} onChange={e => setEditingCompany({...editingCompany, contactPersonName: e.target.value})} /></div>
                        <div className="space-y-2"><Label>{t("settings.masterData.position")}</Label><Input value={editingCompany.contactPersonPosition || ""} onChange={e => setEditingCompany({...editingCompany, contactPersonPosition: e.target.value})} /></div>
                        <div className="space-y-2"><Label>{t("common.email")}</Label><Input type="email" value={editingCompany.contactPersonEmail || ""} onChange={e => setEditingCompany({...editingCompany, contactPersonEmail: e.target.value})} /></div>
                        <div className="space-y-2"><Label>{t("common.phone")}</Label><Input value={editingCompany.contactPersonPhone || ""} onChange={e => setEditingCompany({...editingCompany, contactPersonPhone: e.target.value})} /></div>
                      </div>
                    </div>
                    <div className="border-t pt-4">
                      <h4 className="font-medium mb-3">{t("settings.masterData.additionalInfo")}</h4>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div className="space-y-2"><Label>{t("settings.masterData.registrationNumber")}</Label><Input value={editingCompany.registrationNumber || ""} onChange={e => setEditingCompany({...editingCompany, registrationNumber: e.target.value})} /></div>
                        <div className="space-y-2">
                          <Label>{t("common.status")}</Label>
                          <Select value={editingCompany.status} onValueChange={(v) => setEditingCompany({...editingCompany, status: v})}>
                            <SelectTrigger><SelectValue /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="active">{t("common.active")}</SelectItem>
                              <SelectItem value="inactive">{t("common.inactive")}</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                      <div className="space-y-2 mt-4"><Label>{t("settings.masterData.address")}</Label><Input value={editingCompany.address || ""} onChange={e => setEditingCompany({...editingCompany, address: e.target.value})} /></div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4">
                        <div className="space-y-2"><Label>{t("settings.masterData.city")}</Label><Input value={editingCompany.city || ""} onChange={e => setEditingCompany({...editingCompany, city: e.target.value})} /></div>
                        <div className="space-y-2"><Label>{t("settings.masterData.country")}</Label><Input value={editingCompany.country || ""} onChange={e => setEditingCompany({...editingCompany, country: e.target.value})} /></div>
                      </div>
                      <div className="space-y-2 mt-4"><Label>{t("common.notes")}</Label><Input value={editingCompany.notes || ""} onChange={e => setEditingCompany({...editingCompany, notes: e.target.value})} /></div>
                    </div>
                  </div>
                )}
                <DialogFooter>
                  <Button type="button" variant="outline" onClick={() => setEditCompanyOpen(false)}>{t("common.cancel")}</Button>
                  <Button type="submit" disabled={updateCompanyMutation.isPending}>
                    {updateCompanyMutation.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                    {t("settings.masterData.updateCompany")}
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>

          {/* Edit Main Activity Dialog */}
          <Dialog open={editMainActivityOpen} onOpenChange={setEditMainActivityOpen}>
            <DialogContent>
              <form onSubmit={(e) => { e.preventDefault(); if(editingMainActivity) updateMainActivityMutation.mutate(editingMainActivity); }}>
                <DialogHeader><DialogTitle>{t("settings.masterData.editMainActivity")}</DialogTitle></DialogHeader>
                {editingMainActivity && (
                  <div className="grid gap-4 py-4">
                    <div className="space-y-2"><Label>{t("settings.masterData.activityNameEn")}</Label><Input value={editingMainActivity.name} onChange={e => setEditingMainActivity({...editingMainActivity, name: e.target.value})} required /></div>
                    <div className="space-y-2"><Label>{t("settings.masterData.activityNameAr")}</Label><Input value={editingMainActivity.nameAr || ""} onChange={e => setEditingMainActivity({...editingMainActivity, nameAr: e.target.value})} dir="rtl" /></div>
                    <div className="space-y-2"><Label>{t("common.description")}</Label><Input value={editingMainActivity.description || ""} onChange={e => setEditingMainActivity({...editingMainActivity, description: e.target.value})} /></div>
                    <div className="flex items-center space-x-2">
                      <Switch checked={editingMainActivity.isActive} onCheckedChange={c => setEditingMainActivity({...editingMainActivity, isActive: c})} />
                      <Label>{t("common.active")}</Label>
                    </div>
                  </div>
                )}
                <DialogFooter>
                  <Button type="button" variant="outline" onClick={() => setEditMainActivityOpen(false)}>{t("common.cancel")}</Button>
                  <Button type="submit" className="bg-[#5B2C93] hover:bg-[#5B2C93]" disabled={updateMainActivityMutation.isPending}>
                    {updateMainActivityMutation.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                    {t("common.save")}
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>

          {/* Edit Sub Activity Dialog */}
          <Dialog open={editSubActivityOpen} onOpenChange={setEditSubActivityOpen}>
            <DialogContent>
              <form onSubmit={(e) => { e.preventDefault(); if(editingSubActivity) updateSubActivityMutation.mutate(editingSubActivity); }}>
                <DialogHeader><DialogTitle>{t("settings.masterData.editSubActivity")}</DialogTitle></DialogHeader>
                {editingSubActivity && (
                  <div className="grid gap-4 py-4">
                    <div className="space-y-2"><Label>{t("settings.masterData.subActivityNameEn")}</Label><Input value={editingSubActivity.name} onChange={e => setEditingSubActivity({...editingSubActivity, name: e.target.value})} required /></div>
                    <div className="space-y-2"><Label>{t("settings.masterData.subActivityNameAr")}</Label><Input value={editingSubActivity.nameAr || ""} onChange={e => setEditingSubActivity({...editingSubActivity, nameAr: e.target.value})} dir="rtl" /></div>
                    <div className="space-y-2">
                      <Label>{t("settings.masterData.riskLevel")}</Label>
                      <Select value={editingSubActivity.riskLevel || "low"} onValueChange={v => setEditingSubActivity({...editingSubActivity, riskLevel: v as any})}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="low">{t("settings.masterData.riskLow")}</SelectItem>
                          <SelectItem value="medium">{t("settings.masterData.riskMedium")}</SelectItem>
                          <SelectItem value="high">{t("settings.masterData.riskHigh")}</SelectItem>
                          <SelectItem value="critical">{t("settings.masterData.riskCritical")}</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-3">
                      <Label className="text-sm font-medium">{t("settings.masterData.requirements")}</Label>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <div className="flex items-center space-x-2"><Switch checked={editingSubActivity.needsRFC} onCheckedChange={c => setEditingSubActivity({...editingSubActivity, needsRFC: c})} /><Label className="text-sm">{t("settings.masterData.needsRFC")}</Label></div>
                        <div className="flex items-center space-x-2"><Switch checked={editingSubActivity.needsHRS} onCheckedChange={c => setEditingSubActivity({...editingSubActivity, needsHRS: c})} /><Label className="text-sm">{t("settings.masterData.needsHRS")}</Label></div>
                        <div className="flex items-center space-x-2"><Switch checked={editingSubActivity.needsMOP} onCheckedChange={c => setEditingSubActivity({...editingSubActivity, needsMOP: c})} /><Label className="text-sm">{t("settings.masterData.needsMOP")}</Label></div>
                        <div className="flex items-center space-x-2"><Switch checked={editingSubActivity.needsMHV} onCheckedChange={c => setEditingSubActivity({...editingSubActivity, needsMHV: c})} /><Label className="text-sm">{t("settings.masterData.needsMHV")}</Label></div>
                        <div className="flex items-center space-x-2"><Switch checked={editingSubActivity.needsRoomSelection} onCheckedChange={c => setEditingSubActivity({...editingSubActivity, needsRoomSelection: c})} /><Label className="text-sm">{t("settings.masterData.needsRoom")}</Label></div>
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Switch checked={editingSubActivity.isActive} onCheckedChange={c => setEditingSubActivity({...editingSubActivity, isActive: c})} />
                      <Label>{t("common.active")}</Label>
                    </div>
                  </div>
                )}
                <DialogFooter>
                  <Button type="button" variant="outline" onClick={() => setEditSubActivityOpen(false)}>{t("common.cancel")}</Button>
                  <Button type="submit" className="bg-[#5B2C93] hover:bg-[#5B2C93]" disabled={updateSubActivityMutation.isPending}>
                    {updateSubActivityMutation.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                    {t("common.save")}
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>

          {/* Edit Role Type Dialog */}
          <Dialog open={editRoleTypeOpen} onOpenChange={setEditRoleTypeOpen}>
            <DialogContent>
              <form onSubmit={(e) => { e.preventDefault(); if(editingRoleType) updateRoleTypeMutation.mutate(editingRoleType); }}>
                <DialogHeader><DialogTitle>{t("settings.masterData.editRoleType")}</DialogTitle></DialogHeader>
                {editingRoleType && (
                  <div className="grid gap-4 py-4">
                    <div className="space-y-2"><Label>{t("settings.masterData.roleTypeNameEn")}</Label><Input value={editingRoleType.name} onChange={e => setEditingRoleType({...editingRoleType, name: e.target.value})} required /></div>
                    <div className="space-y-2"><Label>{t("settings.masterData.roleTypeNameAr")}</Label><Input value={editingRoleType.nameAr || ""} onChange={e => setEditingRoleType({...editingRoleType, nameAr: e.target.value})} dir="rtl" /></div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>{t("settings.masterData.category")}</Label>
                        <Select value={editingRoleType.category || "internal"} onValueChange={v => setEditingRoleType({...editingRoleType, category: v as any})}>
                          <SelectTrigger><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="internal">{t("settings.masterData.catInternal")}</SelectItem>
                            <SelectItem value="external">{t("settings.masterData.catExternal")}</SelectItem>
                            <SelectItem value="contractor">{t("settings.masterData.catContractor")}</SelectItem>
                            <SelectItem value="visitor">{t("settings.masterData.catVisitor")}</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label>{t("settings.masterData.accessLevel")}</Label>
                        <Select value={editingRoleType.accessLevel || "standard"} onValueChange={v => setEditingRoleType({...editingRoleType, accessLevel: v as any})}>
                          <SelectTrigger><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="basic">{t("settings.masterData.accessBasic")}</SelectItem>
                            <SelectItem value="standard">{t("settings.masterData.accessStandard")}</SelectItem>
                            <SelectItem value="elevated">{t("settings.masterData.accessElevated")}</SelectItem>
                            <SelectItem value="full">{t("settings.masterData.accessFull")}</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    <div className="space-y-2"><Label>{t("common.description")}</Label><Input value={editingRoleType.description || ""} onChange={e => setEditingRoleType({...editingRoleType, description: e.target.value})} /></div>
                    <div className="flex items-center space-x-2">
                      <Switch checked={editingRoleType.isActive} onCheckedChange={c => setEditingRoleType({...editingRoleType, isActive: c})} />
                      <Label>{t("common.active")}</Label>
                    </div>
                  </div>
                )}
                <DialogFooter>
                  <Button type="button" variant="outline" onClick={() => setEditRoleTypeOpen(false)}>{t("common.cancel")}</Button>
                  <Button type="submit" className="bg-[#5B2C93] hover:bg-[#5B2C93]" disabled={updateRoleTypeMutation.isPending}>
                    {updateRoleTypeMutation.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                    {t("common.save")}
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>

          {/* Edit Approver Dialog */}
          <Dialog open={editApproverOpen} onOpenChange={setEditApproverOpen}>
            <DialogContent>
              <form onSubmit={(e) => { e.preventDefault(); if(editingApprover) updateApproverMutation.mutate({...editingApprover, siteId: editingApprover.siteId ?? undefined}); }}>
                <DialogHeader><DialogTitle>{t("settings.masterData.editApprover")}</DialogTitle></DialogHeader>
                {editingApprover && (
                  <div className="grid gap-4 py-4">
                    <div className="space-y-2">
                      <Label>{t("common.user")}</Label>
                      <Select value={editingApprover.userId?.toString()} onValueChange={v => setEditingApprover({...editingApprover, userId: parseInt(v)})}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {usersForApprovers?.users?.map(u => (
                            <SelectItem key={u.id} value={u.id.toString()}>{u.name || u.email}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>{t("settings.masterData.siteOptional")}</Label>
                      <Select value={editingApprover.siteId?.toString() || "all"} onValueChange={v => setEditingApprover({...editingApprover, siteId: v === "all" ? null : parseInt(v)})}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">{t("settings.masterData.allSites")}</SelectItem>
                          {sitesForApprovers?.map(s => (
                            <SelectItem key={s.id} value={s.id.toString()}>{s.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>{t("settings.masterData.approvalLevel")}</Label>
                      <Input type="number" min={1} max={10} value={editingApprover.approvalLevel} onChange={e => setEditingApprover({...editingApprover, approvalLevel: parseInt(e.target.value) || 1})} />
                    </div>
                    <div className="flex gap-4">
                      <div className="flex items-center space-x-2">
                        <Switch checked={editingApprover.canApproveEmergency} onCheckedChange={c => setEditingApprover({...editingApprover, canApproveEmergency: c})} />
                        <Label>{t("settings.masterData.canApproveEmergency")}</Label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <Switch checked={editingApprover.canApproveVIP} onCheckedChange={c => setEditingApprover({...editingApprover, canApproveVIP: c})} />
                        <Label>{t("settings.masterData.canApproveVIP")}</Label>
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Switch checked={editingApprover.isActive} onCheckedChange={c => setEditingApprover({...editingApprover, isActive: c})} />
                      <Label>{t("common.active")}</Label>
                    </div>
                  </div>
                )}
                <DialogFooter>
                  <Button type="button" variant="outline" onClick={() => setEditApproverOpen(false)}>{t("common.cancel")}</Button>
                  <Button type="submit" className="bg-[#5B2C93] hover:bg-[#5B2C93]" disabled={updateApproverMutation.isPending}>
                    {updateApproverMutation.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                    {t("common.save")}
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>

          {/* Edit Country Dialog */}
          <Dialog open={editCountryOpen} onOpenChange={setEditCountryOpen}>
            <DialogContent>
              <form onSubmit={(e) => { e.preventDefault(); if(editingCountry) updateCountryMutation.mutate(editingCountry); }}>
                <DialogHeader><DialogTitle>{t("settings.masterData.editCountry")}</DialogTitle></DialogHeader>
                {editingCountry && (
                  <div className="grid gap-4 py-4">
                    <div className="space-y-2"><Label>{t("settings.masterData.countryCode")}</Label><Input value={editingCountry.code} onChange={e => setEditingCountry({...editingCountry, code: e.target.value.toUpperCase()})} /></div>
                    <div className="space-y-2"><Label>{t("settings.masterData.countryName")}</Label><Input value={editingCountry.name} onChange={e => setEditingCountry({...editingCountry, name: e.target.value})} required /></div>
                    <div className="flex items-center space-x-2">
                      <Switch checked={editingCountry.isActive} onCheckedChange={c => setEditingCountry({...editingCountry, isActive: c})} />
                      <Label>{t("common.active")}</Label>
                    </div>
                  </div>
                )}
                <DialogFooter>
                  <Button type="button" variant="outline" onClick={() => setEditCountryOpen(false)}>{t("common.cancel")}</Button>
                  <Button type="submit" className="bg-[#5B2C93] hover:bg-[#5B2C93]" disabled={updateCountryMutation.isPending}>
                    {updateCountryMutation.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                    {t("common.save")}
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>

          {/* Edit Region Dialog */}
          <Dialog open={editRegionOpen} onOpenChange={setEditRegionOpen}>
            <DialogContent>
              <form onSubmit={(e) => { e.preventDefault(); if(editingRegion) updateRegionMutation.mutate(editingRegion); }}>
                <DialogHeader><DialogTitle>{t("settings.masterData.editRegion")}</DialogTitle></DialogHeader>
                {editingRegion && (
                  <div className="grid gap-4 py-4">
                    <div className="space-y-2"><Label>{t("settings.masterData.regionCode")}</Label><Input value={editingRegion.code} onChange={e => setEditingRegion({...editingRegion, code: e.target.value.toUpperCase()})} /></div>
                    <div className="space-y-2"><Label>{t("settings.masterData.regionName")}</Label><Input value={editingRegion.name} onChange={e => setEditingRegion({...editingRegion, name: e.target.value})} required /></div>
                    <div className="flex items-center space-x-2">
                      <Switch checked={editingRegion.isActive} onCheckedChange={c => setEditingRegion({...editingRegion, isActive: c})} />
                      <Label>{t("common.active")}</Label>
                    </div>
                  </div>
                )}
                <DialogFooter>
                  <Button type="button" variant="outline" onClick={() => setEditRegionOpen(false)}>{t("common.cancel")}</Button>
                  <Button type="submit" className="bg-[#5B2C93] hover:bg-[#5B2C93]" disabled={updateRegionMutation.isPending}>
                    {updateRegionMutation.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                    {t("common.save")}
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>

          {/* Edit City Dialog */}
          <Dialog open={editCityOpen} onOpenChange={setEditCityOpen}>
            <DialogContent>
              <form onSubmit={(e) => { e.preventDefault(); if(editingCity) updateCityMutation.mutate(editingCity); }}>
                <DialogHeader><DialogTitle>{t("settings.masterData.editCity")}</DialogTitle></DialogHeader>
                {editingCity && (
                  <div className="grid gap-4 py-4">
                    <div className="space-y-2">
                      <Label>{t("settings.masterData.country")}</Label>
                      <Select value={editingCity.countryId?.toString() || ""} onValueChange={(v) => setEditingCity({...editingCity, countryId: parseInt(v)})}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {(countriesData || []).filter(c => c.isActive).map(c => (
                            <SelectItem key={c.id} value={c.id.toString()}>{c.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2"><Label>{t("settings.masterData.cityName")}</Label><Input value={editingCity.name} onChange={e => setEditingCity({...editingCity, name: e.target.value})} required /></div>
                    <div className="flex items-center space-x-2">
                      <Switch checked={editingCity.isActive} onCheckedChange={c => setEditingCity({...editingCity, isActive: c})} />
                      <Label>{t("common.active")}</Label>
                    </div>
                  </div>
                )}
                <DialogFooter>
                  <Button type="button" variant="outline" onClick={() => setEditCityOpen(false)}>{t("common.cancel")}</Button>
                  <Button type="submit" className="bg-[#5B2C93] hover:bg-[#5B2C93]" disabled={updateCityMutation.isPending}>
                    {updateCityMutation.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                    {t("common.save")}
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        </TabsContent>

        {/* ============ SECURITY TAB ============ */}
        <TabsContent value="security" className="mt-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Shield className="h-5 w-5 text-[#5B2C93]" />
                {t("settings.security.title")}
              </CardTitle>
              <CardDescription>{t("settings.security.desc")}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label>{t("settings.security.passwordExpiry")}</Label>
                  <div className="flex items-center gap-2">
                    <Input
                      type="number"
                      min={1}
                      max={365}
                      value={securitySettings.passwordExpiry}
                      onChange={e => setSecuritySettings({...securitySettings, passwordExpiry: parseInt(e.target.value) || 90})}
                      className="w-24"
                    />
                    <span className="text-sm text-[#6B6B6B]">{t("settings.security.days")}</span>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>{t("settings.security.sessionTimeout")}</Label>
                  <div className="flex items-center gap-2">
                    <Input
                      type="number"
                      min={5}
                      max={480}
                      value={securitySettings.sessionTimeout}
                      onChange={e => setSecuritySettings({...securitySettings, sessionTimeout: parseInt(e.target.value) || 30})}
                      className="w-24"
                    />
                    <span className="text-sm text-[#6B6B6B]">{t("settings.security.minutes")}</span>
                  </div>
                </div>
              </div>
              <Separator />
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium">{t("settings.security.mfa")}</p>
                  <p className="text-sm text-[#6B6B6B]">{t("settings.security.mfaDesc")}</p>
                </div>
                <Switch
                  checked={securitySettings.mfaEnabled}
                  onCheckedChange={c => setSecuritySettings({...securitySettings, mfaEnabled: c})}
                />
              </div>
            </CardContent>
            <CardFooter className="bg-[#F5F5F5]/20 border-t px-6 py-4">
              <Button className="ml-auto bg-[#5B2C93] hover:bg-[#5B2C93]" onClick={handleSaveSecuritySettings} disabled={updateSecurityMutation.isPending}>
                {updateSecurityMutation.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                <Save className="mr-2 h-4 w-4" /> {t("common.saveChanges")}
              </Button>
            </CardFooter>
          </Card>
        </TabsContent>

        {/* ============ FORM TEMPLATES TAB ============ */}
        <TabsContent value="forms" className="mt-6">
          <FormTemplateBuilder />
        </TabsContent>

        {/* ============ KNOWLEDGE CENTER TAB ============ */}
        <TabsContent value="knowledge" className="mt-6 space-y-6">
          {/* Upload Form */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Upload className="h-5 w-5 text-[#5B2C93]" />
                {t("settings.knowledge.uploadContent")}
              </CardTitle>
              <CardDescription>{t("settings.knowledge.uploadContentDesc")}</CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleKnowledgeUpload} className="space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>{t("settings.knowledge.title")} *</Label>
                    <Input placeholder={t("settings.knowledge.titlePlaceholder")} value={knowledgeForm.title} onChange={e => setKnowledgeForm({...knowledgeForm, title: e.target.value})} required />
                  </div>
                  <div className="space-y-2">
                    <Label>{t("settings.knowledge.contentType")} *</Label>
                    <Select value={knowledgeForm.contentType} onValueChange={v => setKnowledgeForm({...knowledgeForm, contentType: v as any})}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="PDF"><span className="flex items-center gap-2"><FileText className="h-4 w-4" /> PDF</span></SelectItem>
                        <SelectItem value="Video"><span className="flex items-center gap-2"><VideoIcon className="h-4 w-4" /> Video</span></SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>{t("settings.knowledge.category")} *</Label>
                    <Select value={knowledgeForm.category} onValueChange={v => setKnowledgeForm({...knowledgeForm, category: v})}>
                      <SelectTrigger><SelectValue placeholder={t("settings.knowledge.selectCategory")} /></SelectTrigger>
                      <SelectContent>
                        {[...BUILTIN_CATEGORIES, ...knowledgeCategories.filter((c: any) => !BUILTIN_CATEGORIES.includes(c.name)).map((c: any) => c.name)].map((cat: string) => (
                          <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>{t("settings.knowledge.tags")}</Label>
                    <div className="flex items-center gap-1">
                      <Tag className="h-4 w-4 text-[#6B6B6B]" />
                      <Input placeholder={t("settings.knowledge.tagsPlaceholder")} value={knowledgeForm.tags} onChange={e => setKnowledgeForm({...knowledgeForm, tags: e.target.value})} />
                    </div>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>{t("common.description")}</Label>
                  <Textarea placeholder={t("settings.knowledge.descriptionPlaceholder")} value={knowledgeForm.description} onChange={e => setKnowledgeForm({...knowledgeForm, description: e.target.value})} rows={2} />
                </div>
                <div className="space-y-2">
                  <Label>{t("settings.knowledge.fileOrUrl")}</Label>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <input ref={fileInputRef} type="file" accept={knowledgeForm.contentType === "PDF" ? ".pdf" : "video/*"} className="hidden" onChange={e => setKnowledgeFile(e.target.files?.[0] || null)} />
                      <Button type="button" variant="outline" className="w-full" onClick={() => fileInputRef.current?.click()}>
                        <Upload className="h-4 w-4 mr-2" />
                        {knowledgeFile ? knowledgeFile.name : t("settings.knowledge.chooseFile")}
                      </Button>
                    </div>
                    <div className="space-y-1">
                      <Input placeholder={t("settings.knowledge.urlPlaceholder")} value={knowledgeForm.url} onChange={e => setKnowledgeForm({...knowledgeForm, url: e.target.value})} />
                    </div>
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  <Checkbox id="mandatoryAck" checked={knowledgeForm.mandatoryAck} onCheckedChange={c => setKnowledgeForm({...knowledgeForm, mandatoryAck: c === true})} />
                  <Label htmlFor="mandatoryAck">{t("settings.knowledge.mandatoryAck")}</Label>
                </div>
                <Button type="submit" className="bg-[#5B2C93] hover:bg-[#5B2C93]" disabled={isUploadingKnowledge || createKnowledgeMutation.isPending}>
                  {(isUploadingKnowledge || createKnowledgeMutation.isPending) && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                  {t("settings.knowledge.uploadBtn")}
                </Button>
              </form>
            </CardContent>
          </Card>

          {/* Categories Management */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <BookOpen className="h-5 w-5 text-[#5B2C93]" />
                {t("settings.knowledge.categories")}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex gap-2 mb-4">
                <Input placeholder={t("settings.knowledge.newCategoryPlaceholder")} value={newCategoryName} onChange={e => setNewCategoryName(e.target.value)} className="max-w-xs" />
                <Button onClick={() => { if(newCategoryName.trim()) createCategoryMutation.mutate({ name: newCategoryName.trim() }); }} disabled={!newCategoryName.trim() || createCategoryMutation.isPending}>
                  <Plus className="h-4 w-4 mr-1" /> {t("common.add")}
                </Button>
              </div>
              <div className="flex flex-wrap gap-2">
                {BUILTIN_CATEGORIES.map(cat => (
                  <Badge key={cat} variant="secondary" className="px-3 py-1">{cat}</Badge>
                ))}
                {knowledgeCategories.filter((c: any) => !BUILTIN_CATEGORIES.includes(c.name)).map((cat: any) => (
                  <Badge key={cat.id} variant="outline" className="px-3 py-1 flex items-center gap-1">
                    {cat.name}
                    <button onClick={() => deleteCategoryMutation.mutate({ name: cat.name })} className="ml-1 hover:text-[#FF6B6B]">
                      <XIcon className="h-3 w-3" />
                    </button>
                  </Badge>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Content List */}
          <Card>
            <CardHeader>
              <CardTitle>{t("settings.knowledge.allContent")}</CardTitle>
              <CardDescription>{t("settings.knowledge.allContentDesc", { count: knowledgeContents.length })}</CardDescription>
            </CardHeader>
            <CardContent>
              {knowledgeLoading ? (
                <div className="flex items-center justify-center py-8"><Loader2 className="h-6 w-6 animate-spin text-[#5B2C93]" /></div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>{t("settings.knowledge.title")}</TableHead>
                      <TableHead>{t("settings.knowledge.category")}</TableHead>
                      <TableHead>{t("settings.knowledge.contentType")}</TableHead>
                      <TableHead>{t("common.status")}</TableHead>
                      <TableHead>{t("settings.knowledge.views")}</TableHead>
                      <TableHead className="text-right">{t("common.actions")}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {knowledgeContents.map((item: any) => (
                      <TableRow key={item.id}>
                        <TableCell>
                          <div>
                            <div className="font-medium">{item.title}</div>
                            {item.description && <div className="text-xs text-[#6B6B6B] truncate max-w-xs">{item.description}</div>}
                          </div>
                        </TableCell>
                        <TableCell><Badge variant="outline">{item.category}</Badge></TableCell>
                        <TableCell>
                          <Badge variant="secondary" className="flex items-center gap-1 w-fit">
                            {item.contentType === "PDF" ? <FileText className="h-3 w-3" /> : <VideoIcon className="h-3 w-3" />}
                            {item.contentType}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <Badge variant={item.status === 'PUBLISHED' ? "default" : "secondary"} className={item.status === 'PUBLISHED' ? "bg-[#D1FAE5] text-[#059669]" : ""}>
                            {item.status === 'PUBLISHED' ? t("settings.knowledge.published") : t("settings.knowledge.draft")}
                          </Badge>
                        </TableCell>
                        <TableCell>{item.viewCount || 0}</TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-2">
                            {item.fileUrl && (
                              <Button variant="ghost" size="icon" asChild>
                                <a href={item.fileUrl} target="_blank" rel="noopener noreferrer"><ExternalLink className="h-4 w-4" /></a>
                              </Button>
                            )}
                            <Button variant="ghost" size="icon" onClick={() => updateKnowledgeStatusMutation.mutate({ id: item.id, status: item.status === 'PUBLISHED' ? 'DRAFT' : 'PUBLISHED' as 'DRAFT' | 'PUBLISHED' | 'ARCHIVED' })}>
                              {item.status === 'PUBLISHED' ? <XIcon className="h-4 w-4 text-[#D97706]" /> : <Save className="h-4 w-4 text-[#059669]" />}
                            </Button>
                            <Button variant="ghost" size="icon" onClick={() => { if(confirm(t("settings.knowledge.deleteConfirm"))) deleteKnowledgeMutation.mutate({ id: item.id }); }}>
                              <Trash2 className="h-4 w-4 text-[#FF6B6B]" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                    {knowledgeContents.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={6} className="text-center text-[#6B6B6B] py-8">{t("settings.knowledge.noContent")}</TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
